import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TabNav } from "./components/TabNav";
import { Header } from "./components/Header";
import { HomeTab } from "./components/HomeTab";
import { HelpTab } from "./components/HelpTab";
import { HistoryTab } from "./components/HistoryTab";
import { ChatView } from "./components/ChatView";
import { ClosedFlowCard } from "./components/ClosedFlowCard";
import { FlowDebugModal } from "./components/FlowDebugModal";
import { WidgetProvider } from "./context";
import { createWidgetI18n } from "./i18n";
import { DEFAULT_AGENT_IMAGE, getWidgetAppOrigin, resolveWidgetAssetUrl } from "./config";
import { activityTranslationKey, initialAgentState, reduceAgentEvent } from "./v2/events";
import { startRun, resolveStreamReceipt, ConversationTransport } from "./v2/transport";
export const widgetThemes = {
    light: {
        accentColor: "#2563eb",
        background: "#f4f7fb",
        foreground: "#0f172a",
        primaryTextColor: "#0f172a",
        secondaryTextColor: "#64748b",
        corner: "18px",
        position: "bottom-right",
        secondaryBackground: "#ffffff",
        cardBackgroundColor: "#ffffff",
        activeCardBackgroundColor: "#1f2937",
        homepageGradientBaseColor: "#d9e1ec",
        userMessageBackgroundColor: "#2563eb",
        userMessageTextColor: "#ffffff",
        chatHeaderBackgroundColor: "#e9eef5",
        activeChatBackgroundColor: "#f4f7fb",
        assistantTextColor: "#0f172a",
        closedFlowsBackgroundColor: "#ffffff",
        closedFlowsTextColor: "#0f172a",
        closedFlowsSuggestionColor: "#e2e8f0",
        contentColor: "#0f172a",
    },
    dark: {
        accentColor: "#3b82f6",
        background: "#0f172a",
        foreground: "#e2e8f0",
        primaryTextColor: "#e2e8f0",
        secondaryTextColor: "#93a4bf",
        corner: "18px",
        position: "bottom-right",
        secondaryBackground: "#111c2f",
        cardBackgroundColor: "#111c2f",
        activeCardBackgroundColor: "#334155",
        homepageGradientBaseColor: "#1e293b",
        userMessageBackgroundColor: "#3b82f6",
        userMessageTextColor: "#ffffff",
        chatHeaderBackgroundColor: "#1e293b",
        activeChatBackgroundColor: "#0f172a",
        assistantTextColor: "#e2e8f0",
        closedFlowsBackgroundColor: "#111c2f",
        closedFlowsTextColor: "#e2e8f0",
        closedFlowsSuggestionColor: "#334155",
        contentColor: "#e2e8f0",
    },
};
export const defaultTheme = widgetThemes.light;
const FLOW_SESSION_KEY = "chatbot-widget-flow-session-v1";
const FLOW_LEGACY_KEY = "chatbot-widget-flow-state-v1";
const flowCardKey = "chatbot-closed-flows-shown";
const flowCardLegacyKey = "chatbot-closed-suggestions-shown";
const CUSTOMER_ID_KEY = "chatbot-customer-id";
const SOUND_ENABLED_KEY = "noa-widget-sound-enabled";
const SOUND_VARIANT_KEY = "noa-widget-sound-variant";
const FLOW_REDIRECT_CONTINUE_MESSAGE = "The process is complete. Ask me naturally what I would like to do next, and suggest a few possible next questions I might ask. Write the suggestions in first person, from my perspective — not as things the assistant would say.";
const SOUND_VARIANTS = [
    "classic",
    "soft",
    "pop",
    "bell",
    "glass",
    "pulse",
    "click",
    "spark",
    "ping",
    "chime",
];
const WIDGET_WIDTH_MIN = 320;
const WIDGET_WIDTH_MAX = 520;
const WIDGET_HEIGHT_MIN = 560;
const WIDGET_HEIGHT_MAX = 900;
const CLOSED_FLOWS_DELAY_MIN = 0;
const CLOSED_FLOWS_DELAY_MAX = 30;
const BROWSER_SEARCH_REASONING_EFFORTS = new Set(["low", "medium", "high"]);
const defaultWelcomeMessage = {
    id: "assistant-welcome",
    role: "assistant",
    content: "",
    createdAt: Date.now(),
};
const defaultHistory = [];
const defaultFlowState = {
    conversationId: null,
    flowRunId: null,
    bookingFlowRunId: null,
    selectedFlowId: null,
    selectedPlaybookId: null,
    currentStepId: null,
    flowStatus: null,
    flowIsComplete: null,
    flowCompletionPercent: null,
    flowMissingFields: [],
    flowValidationIssues: [],
    rawCompletion: null,
    lastUpdated: null,
};
function useConsentKey() {
    const key = "chatbot-privacy-approved";
    const [approved, setApproved] = useState(() => {
        if (typeof window === "undefined")
            return false;
        try {
            return window.localStorage.getItem(key) === "true";
        }
        catch {
            return false;
        }
    });
    const approve = () => {
        if (typeof window !== "undefined") {
            try {
                window.localStorage.setItem(key, "true");
            }
            catch {
                // ignore storage exceptions
            }
        }
        setApproved(true);
    };
    return { approved, approve };
}
function createWelcomeMessage(t, brandName, welcome) {
    const content = (welcome === null || welcome === void 0 ? void 0 : welcome.trim()) || t("widget.welcomeMessage", { brandName });
    return {
        ...defaultWelcomeMessage,
        id: `assistant-welcome-${Date.now()}`,
        content,
        createdAt: Date.now(),
    };
}
function buildPreviewChatMessages(t, brandName, welcome, now = Date.now()) {
    return [
        createWelcomeMessage(t, brandName, welcome),
        {
            id: "preview-chat-user",
            role: "user",
            content: "Do you offer support on weekends?",
            createdAt: now - 1000 * 60 * 3,
        },
        {
            id: "preview-chat-assistant",
            role: "assistant",
            content: "Yes, our support team is available 7 days a week from 8AM to 8PM.",
            createdAt: now - 1000 * 60 * 2,
        },
    ];
}
function resolveBrandName(configuration) {
    var _a, _b, _c;
    if (!configuration)
        return null;
    const maybeBrand = (_c = (_b = (_a = configuration === null || configuration === void 0 ? void 0 : configuration.brandName) !== null && _a !== void 0 ? _a : configuration === null || configuration === void 0 ? void 0 : configuration.brand_name) !== null && _b !== void 0 ? _b : configuration === null || configuration === void 0 ? void 0 : configuration.project_name) !== null && _c !== void 0 ? _c : configuration === null || configuration === void 0 ? void 0 : configuration.name;
    if (typeof maybeBrand === "string" && maybeBrand.trim()) {
        return maybeBrand;
    }
    return null;
}
function generateUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}
function normalizeBaseUrl(url) {
    return url.replace(/\/$/, "");
}
function getCurrentPageMetadata(widgetPlacement) {
    if (typeof window === "undefined")
        return { source: "widget" };
    return {
        source: "widget",
        currentPage: window.location.href,
        pathname: window.location.pathname,
        route: window.location.pathname,
        page: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        widgetPlacement,
    };
}
function normalizeSupportId(value) {
    return typeof value === "string" && value.trim() ? value.trim() : null;
}
function normalizeBrowserSearchReasoningEffort(value) {
    return typeof value === "string" && BROWSER_SEARCH_REASONING_EFFORTS.has(value)
        ? value
        : null;
}
function normalizeTextSuggestion(value) {
    if (typeof value === "string")
        return value;
    if (value && typeof value === "object" && typeof value.message === "string") {
        return value.message;
    }
    return "";
}
function parseStructuredMessageContent(content) {
    var _a;
    if (typeof content !== "string")
        return null;
    const trimmed = content.trim();
    if (!trimmed)
        return null;
    const codeBlockMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
    const candidate = ((_a = codeBlockMatch === null || codeBlockMatch === void 0 ? void 0 : codeBlockMatch[1]) === null || _a === void 0 ? void 0 : _a.trim()) || trimmed;
    if (!candidate.startsWith("{"))
        return null;
    try {
        const parsed = JSON.parse(candidate);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    }
    catch {
        return null;
    }
}
function extractStructuredMessageAnswer(content) {
    const parsed = parseStructuredMessageContent(content);
    if (!parsed)
        return null;
    if (typeof parsed.answer === "string" && parsed.answer.trim())
        return parsed.answer.trim();
    if (typeof parsed.content === "string" && parsed.content.trim())
        return parsed.content.trim();
    if (typeof parsed.text === "string" && parsed.text.trim())
        return parsed.text.trim();
    return null;
}
function extractStructuredMessageSuggestions(content) {
    const parsed = parseStructuredMessageContent(content);
    const rawSuggestions = parsed === null || parsed === void 0 ? void 0 : parsed.nextMessageSuggestions;
    if (!Array.isArray(rawSuggestions))
        return [];
    const seen = new Set();
    const suggestions = [];
    rawSuggestions.forEach((item) => {
        const message = normalizeTextSuggestion(item).trim();
        if (!message || seen.has(message))
            return;
        seen.add(message);
        suggestions.push({ message });
    });
    return suggestions.slice(0, 3);
}
function getLastAssistantSuggestions(messages) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message.role !== "assistant")
            continue;
        return Array.isArray(message.messageSuggestions) ? message.messageSuggestions.slice(0, 3) : [];
    }
    return [];
}
function normalizeChatProducts(value) {
    const normalizeList = (items) => Array.isArray(items)
        ? items
            .map((item) => {
            if (!item || typeof item !== "object")
                return null;
            const product = item;
            if (typeof product.title !== "string" || typeof product.product_url !== "string")
                return null;
            return {
                title: product.title,
                product_url: product.product_url,
                main_image_url: typeof product.main_image_url === "string" ? product.main_image_url : undefined,
                price: typeof product.price === "string" ? product.price : undefined,
                description: typeof product.description === "string" ? product.description : undefined,
                match_score: typeof product.match_score === "number" ? product.match_score : null,
            };
        })
            .filter((item) => Boolean(item))
        : [];
    if (Array.isArray(value)) {
        const recommended = normalizeList(value);
        return recommended.length ? { recommended, similar: [] } : undefined;
    }
    if (!value || typeof value !== "object")
        return undefined;
    const source = value;
    const recommended = normalizeList(source.recommended);
    const similar = normalizeList(source.similar);
    if (!recommended.length && !similar.length)
        return undefined;
    return { recommended, similar };
}
function getRestoredSuggestionsFromSessionState(parsed) {
    var _a;
    if (Array.isArray(parsed === null || parsed === void 0 ? void 0 : parsed.nextMessageSuggestions)) {
        return parsed.nextMessageSuggestions
            .map((item) => {
            var _a;
            return item && typeof item === "object"
                ? {
                    message: normalizeTextSuggestion((_a = item.message) !== null && _a !== void 0 ? _a : item).trim(),
                    selectedFlowId: normalizeSupportId(item.selectedFlowId),
                    selectedPlaybookId: normalizeSupportId(item.selectedPlaybookId),
                    currentStepId: normalizeSupportId(item.currentStepId),
                }
                : { message: normalizeTextSuggestion(item).trim() };
        })
            .filter((item) => item.message)
            .slice(0, 3);
    }
    const activeConversationId = typeof (parsed === null || parsed === void 0 ? void 0 : parsed.activeConversationId) === "string" && parsed.activeConversationId
        ? parsed.activeConversationId
        : typeof ((_a = parsed === null || parsed === void 0 ? void 0 : parsed.flowState) === null || _a === void 0 ? void 0 : _a.conversationId) === "string" && parsed.flowState.conversationId
            ? parsed.flowState.conversationId
            : null;
    const conversationMessages = (parsed === null || parsed === void 0 ? void 0 : parsed.conversationMessages) && typeof parsed.conversationMessages === "object"
        ? parsed.conversationMessages
        : null;
    if (activeConversationId && (conversationMessages === null || conversationMessages === void 0 ? void 0 : conversationMessages[activeConversationId])) {
        return getLastAssistantSuggestions(conversationMessages[activeConversationId]);
    }
    if (Array.isArray(parsed === null || parsed === void 0 ? void 0 : parsed.flowMessages)) {
        return getLastAssistantSuggestions(parsed.flowMessages);
    }
    return [];
}
function normalizeChatSuggestions(data, fallbackSupportState) {
    const suggestions = [];
    const seen = new Set();
    const append = (suggestion) => {
        var _a, _b, _c;
        const message = suggestion.message.trim();
        if (!message)
            return;
        const key = `${message}-${(_a = suggestion.selectedFlowId) !== null && _a !== void 0 ? _a : ""}-${(_b = suggestion.selectedPlaybookId) !== null && _b !== void 0 ? _b : ""}-${(_c = suggestion.currentStepId) !== null && _c !== void 0 ? _c : ""}`;
        if (seen.has(key))
            return;
        seen.add(key);
        suggestions.push({ ...suggestion, message });
    };
    const playbookSuggestions = Array.isArray(data.playbook_suggestions)
        ? data.playbook_suggestions
        : Array.isArray(data.support_playbook_suggestions)
            ? data.support_playbook_suggestions
            : [];
    if (playbookSuggestions.length) {
        playbookSuggestions.forEach((item) => {
            var _a;
            if (!item || typeof item !== "object")
                return;
            const record = item;
            const message = typeof record.message === "string" ? record.message : "";
            const selectedPlaybookId = normalizeSupportId((_a = record.playbook_id) !== null && _a !== void 0 ? _a : record.selected_playbook_id);
            append({
                message,
                selectedFlowId: fallbackSupportState.selectedFlowId,
                selectedPlaybookId,
                currentStepId: null,
            });
        });
    }
    const stepSuggestions = Array.isArray(data.step_suggestions)
        ? data.step_suggestions
        : Array.isArray(data.support_step_suggestions)
            ? data.support_step_suggestions
            : [];
    if (stepSuggestions.length) {
        stepSuggestions.forEach((item) => {
            var _a, _b, _c;
            if (!item || typeof item !== "object")
                return;
            const record = item;
            const message = typeof record.message === "string" ? record.message : "";
            const selectedPlaybookId = (_b = normalizeSupportId((_a = record.playbook_id) !== null && _a !== void 0 ? _a : record.selected_playbook_id)) !== null && _b !== void 0 ? _b : fallbackSupportState.selectedPlaybookId;
            const currentStepId = normalizeSupportId((_c = record.step_id) !== null && _c !== void 0 ? _c : record.current_step_id);
            append({
                message,
                selectedFlowId: fallbackSupportState.selectedFlowId,
                selectedPlaybookId,
                currentStepId,
            });
        });
    }
    if (Array.isArray(data.message_suggestions)) {
        data.message_suggestions.forEach((item) => {
            var _a, _b;
            const message = normalizeTextSuggestion(item);
            if (!message.trim())
                return;
            const record = item && typeof item === "object" ? item : null;
            append({
                message,
                selectedFlowId: (_b = normalizeSupportId((_a = record === null || record === void 0 ? void 0 : record.selected_flow_id) !== null && _a !== void 0 ? _a : record === null || record === void 0 ? void 0 : record.flow_id)) !== null && _b !== void 0 ? _b : fallbackSupportState.selectedFlowId,
                selectedPlaybookId: fallbackSupportState.selectedPlaybookId,
                currentStepId: fallbackSupportState.currentStepId,
            });
        });
    }
    if (Array.isArray(data.nextMessageSuggestions)) {
        data.nextMessageSuggestions.forEach((item) => {
            const message = normalizeTextSuggestion(item);
            if (!message.trim())
                return;
            append({
                message,
                selectedFlowId: fallbackSupportState.selectedFlowId,
                selectedPlaybookId: fallbackSupportState.selectedPlaybookId,
                currentStepId: fallbackSupportState.currentStepId,
            });
        });
    }
    return suggestions.slice(0, 3);
}
function normalizeLinkSources(sources) {
    if (!Array.isArray(sources))
        return [];
    const deduped = new Set();
    for (const source of sources) {
        const raw = typeof source === "string"
            ? source
            : source && typeof source === "object" && typeof source.url === "string"
                ? source.url
                : "";
        const trimmed = raw.trim();
        if (!trimmed)
            continue;
        if (!/^https?:\/\//i.test(trimmed))
            continue;
        deduped.add(trimmed);
    }
    return Array.from(deduped);
}
function buildHandoffWebsocketUrl(baseUrl, conversationId, token) {
    const wsBase = normalizeBaseUrl(baseUrl).replace(/^http/, "ws").replace(/\/api\/?$/, "");
    const search = new URLSearchParams({ token, role: "customer" });
    return `${wsBase}/api/chat/ws/conversations/${conversationId}/?${search.toString()}`;
}
function parseDateToMs(value) {
    if (!value)
        return Date.now();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? Date.now() : date.getTime();
}
function formatTimestampLabel(value, justNowLabel) {
    if (!value)
        return justNowLabel;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return justNowLabel;
    return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function parseBooleanSetting(value, fallback) {
    if (typeof value === "boolean")
        return value;
    if (typeof value === "number")
        return value !== 0;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "yes", "on"].includes(normalized))
            return true;
        if (["0", "false", "no", "off"].includes(normalized))
            return false;
    }
    return fallback;
}
function clampWidgetSize(value, min, max, fallback) {
    const numeric = typeof value === "number" ? value : typeof value === "string" ? Number.parseInt(value, 10) : Number.NaN;
    if (!Number.isFinite(numeric))
        return fallback;
    return Math.min(max, Math.max(min, Math.round(numeric)));
}
function parseSoundVariant(value, fallback) {
    const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
    return SOUND_VARIANTS.includes(normalized) ? normalized : fallback;
}
function truncateText(value, maxLength) {
    if (value.length <= maxLength)
        return value;
    return `${value.slice(0, maxLength).trimEnd()}...`;
}
function conversationTitle(conversation, t) {
    var _a;
    const name = ((_a = conversation.name) !== null && _a !== void 0 ? _a : "").trim();
    if (name)
        return name;
    return t("widget.conversation.defaultName", { id: conversation.id.slice(0, 4) });
}
function mapConversationResponse(item) {
    var _a, _b, _c, _d;
    return {
        id: String(item.id),
        customer: String((_a = item.customer) !== null && _a !== void 0 ? _a : ""),
        name: (_b = item.name) !== null && _b !== void 0 ? _b : "",
        createdAt: (_c = item.created_at) !== null && _c !== void 0 ? _c : new Date().toISOString(),
        lastMessageAt: (_d = item.last_message_at) !== null && _d !== void 0 ? _d : null,
        engine: item.engine === "v2" ? "v2" : "v1",
    };
}
function normalizeOpenTab(view, fallback = "home") {
    if (view === "home" || view === "history" || view === "help")
        return view;
    return fallback;
}
function isWidgetHistoryState(value) {
    if (!value || typeof value !== "object")
        return false;
    const state = value;
    return state.__noaWidget === true && typeof state.sessionId === "string" && (state.mode === "open" || state.mode === "chat");
}
function mapConversationMessage(item) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const role = item.role === "admin" ? "admin" : item.role === "assistant" ? "assistant" : "user";
    const sender = (_a = item.sent_by_subaccount) !== null && _a !== void 0 ? _a : null;
    const isV2 = ((_b = item.response_payload) === null || _b === void 0 ? void 0 : _b.engine) === "v2";
    const structuredFromRaw = isV2 ? null : parseStructuredMessageContent((_c = item.raw_content) !== null && _c !== void 0 ? _c : item.content);
    const parsedContent = item.parsed_content && typeof item.parsed_content === "object" && !Array.isArray(item.parsed_content)
        ? item.parsed_content
        : structuredFromRaw;
    const responsePayload = item.response_payload && typeof item.response_payload === "object" && !Array.isArray(item.response_payload)
        ? item.response_payload
        : null;
    const payload = responsePayload || parsedContent || structuredFromRaw;
    const answer = role === "assistant"
        ? isV2 ? item.content : typeof item.answer === "string" && item.answer.trim()
            ? item.answer.trim()
            : typeof (responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.answer) === "string" && responsePayload.answer.trim()
                ? responsePayload.answer.trim()
                : typeof (parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.answer) === "string" && parsedContent.answer.trim()
                    ? parsedContent.answer.trim()
                    : extractStructuredMessageAnswer((_d = item.raw_content) !== null && _d !== void 0 ? _d : item.content)
        : null;
    const messageSuggestions = role === "assistant"
        ? normalizeChatSuggestions({
            message_suggestions: (_f = (_e = item.message_suggestions) !== null && _e !== void 0 ? _e : responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.message_suggestions) !== null && _f !== void 0 ? _f : parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.message_suggestions,
            nextMessageSuggestions: parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.nextMessageSuggestions,
            playbook_suggestions: payload === null || payload === void 0 ? void 0 : payload.playbook_suggestions,
            step_suggestions: payload === null || payload === void 0 ? void 0 : payload.step_suggestions,
        }, {
            selectedFlowId: normalizeSupportId(responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.selected_flow_id),
            selectedPlaybookId: normalizeSupportId(payload === null || payload === void 0 ? void 0 : payload.selected_playbook_id),
            currentStepId: normalizeSupportId(payload === null || payload === void 0 ? void 0 : payload.current_step_id),
        })
        : [];
    const calendarPayload = (payload === null || payload === void 0 ? void 0 : payload.message_type) === "calendar" &&
        (payload === null || payload === void 0 ? void 0 : payload.calendar) &&
        typeof payload.calendar === "object" &&
        Array.isArray(payload.calendar.slots)
        ? {
            text: typeof payload.calendar.text === "string" ? payload.calendar.text : undefined,
            timezone: typeof payload.calendar.timezone === "string"
                ? payload.calendar.timezone
                : undefined,
            duration_minutes: typeof payload.calendar.duration_minutes === "number"
                ? payload.calendar.duration_minutes
                : undefined,
            slots: payload.calendar.slots.filter((slot) => slot && typeof slot.start === "string" && typeof slot.end === "string"),
        }
        : null;
    const products = role === "assistant"
        ? normalizeChatProducts((_h = (_g = item.products) !== null && _g !== void 0 ? _g : responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.products) !== null && _h !== void 0 ? _h : parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.products)
        : undefined;
    const booking = responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.booking;
    return {
        id: isV2 && (responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.run_id) ? `v2-${responsePayload.run_id}` : String(item.id),
        role,
        content: answer !== null && answer !== void 0 ? answer : item.content,
        rawContent: (_j = item.raw_content) !== null && _j !== void 0 ? _j : item.content,
        sources: normalizeLinkSources((_l = (_k = item.sources) !== null && _k !== void 0 ? _k : responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.sources) !== null && _l !== void 0 ? _l : parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.sources),
        messageType: calendarPayload ? "calendar" : undefined,
        calendar: calendarPayload !== null && calendarPayload !== void 0 ? calendarPayload : undefined,
        bookingFlowRunId: normalizeSupportId((_m = responsePayload === null || responsePayload === void 0 ? void 0 : responsePayload.booking_flow_run_id) !== null && _m !== void 0 ? _m : parsedContent === null || parsedContent === void 0 ? void 0 : parsedContent.booking_flow_run_id),
        bookingStatus: (booking === null || booking === void 0 ? void 0 : booking.confirmed) ? "confirmed" : calendarPayload ? "pending" : undefined,
        bookingDetails: (booking === null || booking === void 0 ? void 0 : booking.start) && (booking === null || booking === void 0 ? void 0 : booking.end) ? { start: booking.start, end: booking.end } : undefined,
        createdAt: parseDateToMs(item.created_at),
        senderName: sender === null || sender === void 0 ? void 0 : sender.username,
        senderImage: (_o = sender === null || sender === void 0 ? void 0 : sender.image_url) !== null && _o !== void 0 ? _o : null,
        messageSuggestions,
        products,
    };
}
export function WidgetApp({ options, }) {
    var _a, _b;
    const { theme, baseUrl, showSuggestionsWhenClosed: showFlowsWhenClosed } = options;
    const selectedThemeName = options.themeName === "dark" ? "dark" : "light";
    const isPreview = options.preview === true;
    const [brandName, setBrandName] = useState(options.brandName);
    const [configuration, setConfiguration] = useState(null);
    const [authState, setAuthState] = useState("loading");
    const [authError, setAuthError] = useState(null);
    const [authMessage, setAuthMessage] = useState(null);
    const [accessMode, setAccessMode] = useState("active");
    const [accessMessageOnSend, setAccessMessageOnSend] = useState(null);
    const flowSessionKey = options.testWidget ? `${FLOW_SESSION_KEY}-test-${options.projectId}-${options.engine || "project"}` : FLOW_SESSION_KEY;
    const [customerId, setCustomerId] = useState(null);
    const [conversationEngine, setConversationEngine] = useState("v1");
    const [v2Session, setV2Session] = useState(null);
    const [v2Debug, setV2Debug] = useState(null);
    const [v2Activity, setV2Activity] = useState(null);
    const v2RunRef = useRef(null);
    const v2RequestRef = useRef(null);
    const v2TransportRef = useRef(null);
    const v2EnginesRef = useRef({});
    const [view, setView] = useState("home");
    const [isOpen, setIsOpen] = useState(isPreview ? true : !options.floating);
    const [lastEntryTab, setLastEntryTab] = useState("home");
    const [flowInput, setFlowInput] = useState("");
    const draftSuggestionRef = useRef(null);
    const [conversations, setConversations] = useState([]);
    const [conversationMessages, setConversationMessages] = useState({});
    const [activeConversationId, setActiveConversationId] = useState(null);
    useEffect(() => {
        draftSuggestionRef.current = null;
    }, [activeConversationId]);
    useEffect(() => {
        if (!flowInput)
            draftSuggestionRef.current = null;
    }, [flowInput]);
    const [pendingConversationId, setPendingConversationId] = useState(null);
    const activeConversationRef = useRef(null);
    const flowLastSendRef = useRef(null);
    const lastFlowRequestRef = useRef(null);
    const lastFailedFlowRequestRef = useRef(null);
    const flowSendLockRef = useRef(false);
    const [flowMessages, setFlowMessages] = useState(() => {
        var _a;
        const previewWelcome = (_a = options.previewConfiguration) === null || _a === void 0 ? void 0 : _a.welcome_message;
        const content = typeof previewWelcome === "string" ? previewWelcome.trim() : "";
        return [
            {
                ...defaultWelcomeMessage,
                id: `assistant-welcome-${Date.now()}`,
                content,
                createdAt: Date.now(),
            },
        ];
    });
    const [history, setHistory] = useState(defaultHistory);
    const [flowStreamingId, setFlowStreamingId] = useState(null);
    const [flowPendingPrompt, setFlowPendingPrompt] = useState(null);
    const [flowLoading, setFlowLoading] = useState(false);
    const [flowState, setFlowState] = useState(defaultFlowState);
    const [flowStatesByConversation, setFlowStatesByConversation] = useState({});
    const [handoffActive, setHandoffActive] = useState(false);
    const [handoffToken, setHandoffToken] = useState(null);
    const [handoffExpiresAt, setHandoffExpiresAt] = useState(null);
    const [handoffStatus, setHandoffStatus] = useState("idle");
    const [handoffAgent, setHandoffAgent] = useState(null);
    const [handoffConversationId, setHandoffConversationId] = useState(null);
    const [flowInfoOpen, setFlowInfoOpen] = useState(false);
    const [nextMessageSuggestions, setNextMessageSuggestions] = useState([]);
    const [flowsDismissed, setFlowsDismissed] = useState(() => {
        if (options.preview)
            return false;
        if (typeof window === "undefined")
            return false;
        return (window.sessionStorage.getItem(flowCardKey) === "true" ||
            window.sessionStorage.getItem(flowCardLegacyKey) === "true");
    });
    const [flowBadgesDismissed, setFlowBadgesDismissed] = useState(false);
    const [flowCardVisible, setFlowCardVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [mobileViewportHeight, setMobileViewportHeight] = useState(0);
    const [mobileViewportOffsetTop, setMobileViewportOffsetTop] = useState(0);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [browserSearchReasoningEffort, setBrowserSearchReasoningEffort] = useState(() => {
        const raw = typeof options.browserSearchReasoningEffort === "function"
            ? options.browserSearchReasoningEffort()
            : options.browserSearchReasoningEffort;
        return normalizeBrowserSearchReasoningEffort(raw) || "medium";
    });
    const [hasLocalSoundPreference, setHasLocalSoundPreference] = useState(() => {
        if (typeof window === "undefined")
            return false;
        try {
            const saved = window.localStorage.getItem(SOUND_ENABLED_KEY);
            return saved === "0" || saved === "1";
        }
        catch {
            return false;
        }
    });
    const [soundEnabled, setSoundEnabled] = useState(() => {
        if (typeof window === "undefined")
            return true;
        try {
            const saved = window.localStorage.getItem(SOUND_ENABLED_KEY);
            if (saved === "0")
                return false;
            if (saved === "1")
                return true;
        }
        catch {
            // ignore storage exceptions
        }
        return true;
    });
    const [hasLocalSoundVariantPreference, setHasLocalSoundVariantPreference] = useState(() => {
        if (typeof window === "undefined")
            return false;
        try {
            const saved = window.localStorage.getItem(SOUND_VARIANT_KEY);
            return SOUND_VARIANTS.includes((saved || ""));
        }
        catch {
            return false;
        }
    });
    const [soundVariant, setSoundVariant] = useState(() => {
        if (typeof window === "undefined")
            return "classic";
        try {
            const saved = window.localStorage.getItem(SOUND_VARIANT_KEY);
            return parseSoundVariant(saved, "classic");
        }
        catch {
            return "classic";
        }
    });
    const consent = useConsentKey();
    const bypassConsentRef = useRef(false);
    const handoffSocketRef = useRef(null);
    const handoffIntroShownRef = useRef({});
    const previousFlowMessagesRef = useRef([]);
    const pendingNotificationSoundRef = useRef(false);
    const audioContextRef = useRef(null);
    const historySessionIdRef = useRef(`noa-widget-${generateUuid()}`);
    const widgetUiStateRef = useRef({
        isOpen,
        view,
        lastEntryTab,
    });
    const configPosition = useMemo(() => {
        const val = configuration === null || configuration === void 0 ? void 0 : configuration.position;
        if (val === "left")
            return "bottom-left";
        if (val === "right")
            return "bottom-right";
        return null;
    }, [configuration]);
    const configCustomization = useMemo(() => {
        const rawRoot = (configuration === null || configuration === void 0 ? void 0 : configuration.widget_customization) &&
            typeof configuration.widget_customization === "object"
            ? configuration.widget_customization
            : {};
        const themedRaw = rawRoot[selectedThemeName] &&
            typeof rawRoot[selectedThemeName] === "object" &&
            !Array.isArray(rawRoot[selectedThemeName])
            ? rawRoot[selectedThemeName]
            : null;
        const fallbackLightRaw = selectedThemeName === "dark" &&
            rawRoot.light &&
            typeof rawRoot.light === "object" &&
            !Array.isArray(rawRoot.light)
            ? rawRoot.light
            : null;
        const raw = themedRaw || fallbackLightRaw || rawRoot;
        const pick = (key) => typeof raw[key] === "string" && raw[key].trim() ? raw[key].trim() : null;
        return {
            background: pick("background") || pick("primary_bg"),
            homepageGradientBaseColor: pick("homepage_gradient_base_color"),
            cardBackgroundColor: pick("card_background_color"),
            activeCardBackgroundColor: pick("active_card_background_color") || pick("button_bg"),
            userMessageBackgroundColor: pick("user_message_bg_color"),
            userMessageTextColor: pick("user_message_text_color"),
            chatHeaderBackgroundColor: pick("chat_header_background_color"),
            activeChatBackgroundColor: pick("active_chat_background_color"),
            assistantTextColor: pick("assistant_text_color"),
            closedFlowsBackgroundColor: pick("closed_flows_background_color"),
            closedFlowsTextColor: pick("closed_flows_text_color"),
            closedFlowsSuggestionColor: pick("closed_flows_suggestion_color"),
            primaryTextColor: pick("primary_text_color") || pick("text_color"),
            secondaryTextColor: pick("secondary_text_color"),
        };
    }, [configuration, selectedThemeName]);
    const widgetSettings = useMemo(() => {
        const rawRoot = (configuration === null || configuration === void 0 ? void 0 : configuration.widget_customization) &&
            typeof configuration.widget_customization === "object"
            ? configuration.widget_customization
            : {};
        const hasConfiguredSound = rawRoot.sound_enabled !== undefined && rawRoot.sound_enabled !== null;
        const hasConfiguredSoundVariant = rawRoot.sound_variant !== undefined && rawRoot.sound_variant !== null;
        return {
            hasConfiguredSound,
            hasConfiguredSoundVariant,
            soundEnabled: parseBooleanSetting(rawRoot.sound_enabled, true),
            soundVariant: parseSoundVariant(rawRoot.sound_variant, "classic"),
            closedFlowsPopupDelaySeconds: clampWidgetSize(rawRoot.closed_flows_popup_delay_seconds, CLOSED_FLOWS_DELAY_MIN, CLOSED_FLOWS_DELAY_MAX, 3),
            width: clampWidgetSize(rawRoot.widget_width, WIDGET_WIDTH_MIN, WIDGET_WIDTH_MAX, 360),
            height: clampWidgetSize(rawRoot.widget_height, WIDGET_HEIGHT_MIN, WIDGET_HEIGHT_MAX, 720),
        };
    }, [configuration]);
    const activeTheme = useMemo(() => {
        var _a;
        const palette = widgetThemes[selectedThemeName];
        const primaryText = configCustomization.primaryTextColor ||
            theme.primaryTextColor ||
            palette.primaryTextColor ||
            palette.foreground;
        const cardBackground = configCustomization.cardBackgroundColor ||
            theme.cardBackgroundColor ||
            palette.cardBackgroundColor ||
            palette.secondaryBackground;
        return {
            ...palette,
            ...theme,
            corner: theme.corner || palette.corner,
            position: (_a = configPosition !== null && configPosition !== void 0 ? configPosition : theme.position) !== null && _a !== void 0 ? _a : palette.position,
            background: configCustomization.background || theme.background || palette.background,
            homepageGradientBaseColor: configCustomization.homepageGradientBaseColor ||
                theme.homepageGradientBaseColor ||
                palette.homepageGradientBaseColor,
            cardBackgroundColor: cardBackground,
            activeCardBackgroundColor: configCustomization.activeCardBackgroundColor ||
                theme.activeCardBackgroundColor ||
                palette.activeCardBackgroundColor,
            secondaryBackground: cardBackground,
            userMessageBackgroundColor: configCustomization.userMessageBackgroundColor ||
                theme.userMessageBackgroundColor ||
                palette.userMessageBackgroundColor,
            userMessageTextColor: configCustomization.userMessageTextColor ||
                theme.userMessageTextColor ||
                palette.userMessageTextColor,
            chatHeaderBackgroundColor: configCustomization.chatHeaderBackgroundColor ||
                theme.chatHeaderBackgroundColor ||
                palette.chatHeaderBackgroundColor,
            activeChatBackgroundColor: configCustomization.activeChatBackgroundColor ||
                theme.activeChatBackgroundColor ||
                palette.activeChatBackgroundColor,
            assistantTextColor: configCustomization.assistantTextColor ||
                theme.assistantTextColor ||
                palette.assistantTextColor,
            closedFlowsBackgroundColor: configCustomization.closedFlowsBackgroundColor ||
                theme.closedFlowsBackgroundColor ||
                palette.closedFlowsBackgroundColor,
            closedFlowsTextColor: configCustomization.closedFlowsTextColor ||
                theme.closedFlowsTextColor ||
                palette.closedFlowsTextColor,
            closedFlowsSuggestionColor: configCustomization.closedFlowsSuggestionColor ||
                theme.closedFlowsSuggestionColor ||
                palette.closedFlowsSuggestionColor,
            primaryTextColor: primaryText,
            secondaryTextColor: configCustomization.secondaryTextColor || theme.secondaryTextColor || palette.secondaryTextColor,
            foreground: primaryText,
            contentColor: primaryText,
        };
    }, [configCustomization, configPosition, selectedThemeName, theme]);
    const agentImage = useMemo(() => {
        const val = configuration === null || configuration === void 0 ? void 0 : configuration.agent_image;
        return typeof val === "string" && val.trim() ? val.trim() : null;
    }, [configuration]);
    const resolvePreviewAsset = useCallback((value) => {
        const trimmed = typeof value === "string" ? value.trim() : "";
        if (!trimmed)
            return null;
        if (isPreview && trimmed.startsWith("/"))
            return trimmed;
        return resolveWidgetAssetUrl(trimmed);
    }, [isPreview]);
    const launcherImage = resolvePreviewAsset(agentImage) || resolvePreviewAsset(DEFAULT_AGENT_IMAGE) || DEFAULT_AGENT_IMAGE;
    const resolvedAgentName = useMemo(() => {
        const fromConfig = configuration === null || configuration === void 0 ? void 0 : configuration.agent_name;
        if (typeof fromConfig === "string" && fromConfig.trim())
            return fromConfig.trim();
        return (brandName === null || brandName === void 0 ? void 0 : brandName.trim()) || "Personal Assistant";
    }, [brandName, configuration]);
    const activeLanguage = useMemo(() => {
        if (typeof options.language === "string" && options.language.trim())
            return options.language.trim();
        const fromConfig = (configuration === null || configuration === void 0 ? void 0 : configuration.active_language) ||
            (configuration === null || configuration === void 0 ? void 0 : configuration.language) ||
            (configuration === null || configuration === void 0 ? void 0 : configuration.main_language);
        if (typeof fromConfig === "string" && fromConfig.trim())
            return fromConfig.trim();
        return null;
    }, [configuration, options.language]);
    const i18n = useMemo(() => createWidgetI18n(activeLanguage, configuration === null || configuration === void 0 ? void 0 : configuration.main_language), [activeLanguage, configuration]);
    const t = i18n.t;
    const homeHeroTitle = useMemo(() => {
        const fromConfig = configuration === null || configuration === void 0 ? void 0 : configuration.homepage_hero_title;
        if (typeof fromConfig === "string" && fromConfig.trim())
            return fromConfig.trim();
        return t("widget.homeHeroTitle", { brandName: resolvedAgentName });
    }, [configuration, resolvedAgentName, t]);
    const homeHeroSubtitle = useMemo(() => {
        const fromConfig = configuration === null || configuration === void 0 ? void 0 : configuration.homepage_hero_subtitle;
        if (typeof fromConfig === "string" && fromConfig.trim())
            return fromConfig.trim();
        const welcome = configuration === null || configuration === void 0 ? void 0 : configuration.welcome_message;
        if (typeof welcome === "string" && welcome.trim())
            return welcome.trim();
        return t("widget.homeHeroSubtitle");
    }, [configuration, t]);
    const welcomeMessageText = useMemo(() => {
        const welcome = configuration === null || configuration === void 0 ? void 0 : configuration.welcome_message;
        if (typeof welcome === "string" && welcome.trim())
            return welcome.trim();
        return t("widget.welcomeMessage", { brandName: resolvedAgentName });
    }, [configuration, resolvedAgentName, t]);
    const flowBadges = useMemo(() => {
        const rawFlows = Array.isArray(configuration === null || configuration === void 0 ? void 0 : configuration.flows) ? configuration === null || configuration === void 0 ? void 0 : configuration.flows : [];
        return rawFlows.reduce((acc, flow) => {
            var _a;
            const rawId = flow === null || flow === void 0 ? void 0 : flow.id;
            const id = rawId ? String(rawId) : "";
            const showInStarterConversationCards = (_a = flow === null || flow === void 0 ? void 0 : flow.show_in_starter_conversation_cards) !== null && _a !== void 0 ? _a : true;
            const attraction = typeof (flow === null || flow === void 0 ? void 0 : flow.userAtractionMessage) === "string"
                ? flow.userAtractionMessage.trim()
                : typeof (flow === null || flow === void 0 ? void 0 : flow.userAttractionMessage) === "string"
                    ? flow.userAttractionMessage.trim()
                    : "";
            const legacyAttraction = typeof (flow === null || flow === void 0 ? void 0 : flow.user_attraction_message) === "string" ? flow.user_attraction_message.trim() : "";
            const name = typeof (flow === null || flow === void 0 ? void 0 : flow.name) === "string" ? flow.name.trim() : "";
            const label = attraction || legacyAttraction || name;
            if (!id || !label || showInStarterConversationCards === false)
                return acc;
            acc.push({ id, label });
            return acc;
        }, []);
    }, [configuration]);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        if (!hasLocalSoundPreference)
            return;
        try {
            window.localStorage.setItem(SOUND_ENABLED_KEY, soundEnabled ? "1" : "0");
        }
        catch {
            // ignore storage exceptions
        }
    }, [hasLocalSoundPreference, soundEnabled]);
    useEffect(() => {
        if (hasLocalSoundPreference)
            return;
        if (!widgetSettings.hasConfiguredSound)
            return;
        setSoundEnabled(widgetSettings.soundEnabled);
    }, [hasLocalSoundPreference, widgetSettings.hasConfiguredSound, widgetSettings.soundEnabled]);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        if (!hasLocalSoundVariantPreference)
            return;
        try {
            window.localStorage.setItem(SOUND_VARIANT_KEY, soundVariant);
        }
        catch {
            // ignore storage exceptions
        }
    }, [hasLocalSoundVariantPreference, soundVariant]);
    useEffect(() => {
        if (hasLocalSoundVariantPreference)
            return;
        if (!widgetSettings.hasConfiguredSoundVariant)
            return;
        setSoundVariant(widgetSettings.soundVariant);
    }, [hasLocalSoundVariantPreference, widgetSettings.hasConfiguredSoundVariant, widgetSettings.soundVariant]);
    const toggleSoundPreference = useCallback(() => {
        setHasLocalSoundPreference(true);
        setSoundEnabled((prev) => !prev);
    }, []);
    const selectSoundVariant = useCallback((variant) => {
        setHasLocalSoundVariantPreference(true);
        setSoundVariant(variant);
    }, []);
    const getOrCreateAudioContext = useCallback(() => {
        if (typeof window === "undefined")
            return null;
        if (audioContextRef.current)
            return audioContextRef.current;
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass)
            return null;
        const ctx = new AudioContextClass();
        audioContextRef.current = ctx;
        return ctx;
    }, []);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const unlockAudio = () => {
            const ctx = getOrCreateAudioContext();
            if (!ctx)
                return;
            if (ctx.state === "suspended") {
                void ctx.resume().catch(() => {
                    // ignore unlock failures; retry on next interaction
                });
            }
        };
        window.addEventListener("pointerdown", unlockAudio, { passive: true });
        window.addEventListener("keydown", unlockAudio, { passive: true });
        window.addEventListener("touchstart", unlockAudio, { passive: true });
        void Promise.resolve().then(unlockAudio);
        return () => {
            window.removeEventListener("pointerdown", unlockAudio);
            window.removeEventListener("keydown", unlockAudio);
            window.removeEventListener("touchstart", unlockAudio);
        };
    }, [getOrCreateAudioContext]);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const tryAutoUnlock = () => {
            const ctx = getOrCreateAudioContext();
            if (!ctx || ctx.state === "running")
                return;
            void ctx.resume().catch(() => {
                // blocked by autoplay policy; keep retrying on lifecycle events
            });
        };
        tryAutoUnlock();
        const timer = window.setInterval(tryAutoUnlock, 1200);
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                tryAutoUnlock();
            }
        };
        const onFocus = () => tryAutoUnlock();
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("focus", onFocus);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("focus", onFocus);
        };
    }, [getOrCreateAudioContext]);
    useEffect(() => {
        return () => {
            const ctx = audioContextRef.current;
            audioContextRef.current = null;
            if (ctx) {
                void ctx.close().catch(() => {
                    // ignore close failures
                });
            }
        };
    }, []);
    const playNotificationSound = useCallback((variantOverride) => {
        if (typeof window === "undefined")
            return;
        try {
            const ctx = getOrCreateAudioContext();
            if (!ctx)
                return false;
            const selectedVariant = variantOverride || soundVariant;
            const startTone = () => {
                const now = ctx.currentTime;
                const playNote = (frequency, durationSec, volume, offsetSec, type) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = type;
                    osc.frequency.value = frequency;
                    gain.gain.value = 0.0001;
                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    const startAt = now + offsetSec;
                    gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.01);
                    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec);
                    osc.start(startAt);
                    osc.stop(startAt + durationSec + 0.01);
                };
                const soundMap = {
                    classic: () => playNote(980, 0.17, 0.16, 0, "triangle"),
                    soft: () => {
                        playNote(740, 0.18, 0.09, 0, "sine");
                        playNote(880, 0.16, 0.08, 0.08, "sine");
                    },
                    pop: () => {
                        playNote(620, 0.08, 0.14, 0, "square");
                        playNote(980, 0.11, 0.13, 0.09, "square");
                    },
                    bell: () => {
                        playNote(1040, 0.2, 0.1, 0, "triangle");
                        playNote(1560, 0.14, 0.06, 0.05, "sine");
                    },
                    glass: () => {
                        playNote(1200, 0.12, 0.08, 0, "sine");
                        playNote(1700, 0.09, 0.06, 0.06, "triangle");
                    },
                    pulse: () => {
                        playNote(520, 0.08, 0.11, 0, "square");
                        playNote(520, 0.08, 0.11, 0.11, "square");
                    },
                    click: () => playNote(760, 0.05, 0.13, 0, "square"),
                    spark: () => {
                        playNote(880, 0.07, 0.1, 0, "triangle");
                        playNote(1320, 0.07, 0.09, 0.05, "triangle");
                        playNote(1760, 0.08, 0.08, 0.1, "sine");
                    },
                    ping: () => playNote(1100, 0.13, 0.12, 0, "sine"),
                    chime: () => {
                        playNote(660, 0.14, 0.1, 0, "sine");
                        playNote(990, 0.14, 0.08, 0.08, "sine");
                        playNote(1320, 0.14, 0.07, 0.16, "sine");
                    },
                };
                soundMap[selectedVariant]();
            };
            if (ctx.state === "running") {
                startTone();
                return true;
            }
            if (ctx.state === "suspended") {
                void ctx
                    .resume()
                    .then(() => {
                    startTone();
                })
                    .catch(() => {
                    pendingNotificationSoundRef.current = true;
                });
                return true;
            }
            return false;
        }
        catch {
            // audio may be blocked by browser policy
            return false;
        }
    }, [getOrCreateAudioContext, soundVariant]);
    useEffect(() => {
        const previous = previousFlowMessagesRef.current;
        const current = flowMessages;
        previousFlowMessagesRef.current = current;
        if (!soundEnabled)
            return;
        if (!current.length)
            return;
        const isIncomingSupport = (message) => Boolean(message &&
            (message.role === "assistant" || message.role === "admin") &&
            !String(message.id).startsWith("assistant-welcome"));
        if (current.length === previous.length + 1 &&
            previous.every((msg, idx) => { var _a; return msg.id === ((_a = current[idx]) === null || _a === void 0 ? void 0 : _a.id); })) {
            const appended = current[current.length - 1];
            if (isIncomingSupport(appended)) {
                const played = playNotificationSound();
                if (!played)
                    pendingNotificationSoundRef.current = true;
            }
        }
    }, [flowMessages, playNotificationSound, soundEnabled]);
    useEffect(() => {
        if (typeof window === "undefined" || !soundEnabled)
            return;
        const retryPendingSound = () => {
            if (!pendingNotificationSoundRef.current)
                return;
            const played = playNotificationSound();
            if (played)
                pendingNotificationSoundRef.current = false;
        };
        window.addEventListener("pointerdown", retryPendingSound, { passive: true });
        window.addEventListener("keydown", retryPendingSound, { passive: true });
        window.addEventListener("touchstart", retryPendingSound, { passive: true });
        return () => {
            window.removeEventListener("pointerdown", retryPendingSound);
            window.removeEventListener("keydown", retryPendingSound);
            window.removeEventListener("touchstart", retryPendingSound);
        };
    }, [playNotificationSound, soundEnabled]);
    useEffect(() => {
        var _a, _b, _c, _d;
        const desired = welcomeMessageText;
        if (flowMessages.length === 1 &&
            ((_a = flowMessages[0]) === null || _a === void 0 ? void 0 : _a.role) === "assistant" &&
            ((_c = (_b = flowMessages[0]) === null || _b === void 0 ? void 0 : _b.id) === null || _c === void 0 ? void 0 : _c.startsWith("assistant-welcome")) &&
            ((_d = flowMessages[0]) === null || _d === void 0 ? void 0 : _d.content) !== desired) {
            setFlowMessages([createWelcomeMessage(t, resolvedAgentName, welcomeMessageText)]);
        }
    }, [welcomeMessageText, resolvedAgentName, flowMessages]);
    useEffect(() => {
        const fromConfig = (configuration === null || configuration === void 0 ? void 0 : configuration.agent_name) || resolveBrandName(configuration);
        if (typeof fromConfig === "string" && fromConfig.trim()) {
            setBrandName(fromConfig.trim());
        }
        else if (!brandName) {
            setBrandName("Personal Assistant");
        }
    }, [brandName, configuration]);
    useEffect(() => {
        var _a, _b, _c, _d;
        if (isPreview) {
            const now = Date.now();
            const previewLastConversation = (_a = options.previewConfiguration) === null || _a === void 0 ? void 0 : _a.last_conversation;
            const previewConversationTitle = typeof (previewLastConversation === null || previewLastConversation === void 0 ? void 0 : previewLastConversation.title) === "string" && previewLastConversation.title.trim()
                ? previewLastConversation.title.trim()
                : "Shipping question";
            const previewUserMessage = typeof (previewLastConversation === null || previewLastConversation === void 0 ? void 0 : previewLastConversation.user_message) === "string" && previewLastConversation.user_message.trim()
                ? previewLastConversation.user_message.trim()
                : "How much is express shipping to New York?";
            const previewAssistantAnswer = typeof (previewLastConversation === null || previewLastConversation === void 0 ? void 0 : previewLastConversation.assistant_answer) === "string" && previewLastConversation.assistant_answer.trim()
                ? previewLastConversation.assistant_answer.trim()
                : "Express shipping is usually 1-2 business days and starts from $14.";
            const previewConversations = [
                {
                    id: "preview-conversation-1",
                    customer: "preview",
                    name: previewConversationTitle,
                    createdAt: new Date(now - 1000 * 60 * 60 * 22).toISOString(),
                    lastMessageAt: new Date(now - 1000 * 60 * 8).toISOString(),
                },
                {
                    id: "preview-conversation-2",
                    customer: "preview",
                    name: "Order update",
                    createdAt: new Date(now - 1000 * 60 * 60 * 40).toISOString(),
                    lastMessageAt: new Date(now - 1000 * 60 * 95).toISOString(),
                },
                {
                    id: "preview-conversation-3",
                    customer: "preview",
                    name: "Refund policy",
                    createdAt: new Date(now - 1000 * 60 * 60 * 66).toISOString(),
                    lastMessageAt: new Date(now - 1000 * 60 * 60 * 11).toISOString(),
                },
            ];
            const previewMessagesByConversation = {
                "preview-conversation-1": [
                    {
                        id: "preview-1-user",
                        role: "user",
                        content: previewUserMessage,
                        createdAt: now - 1000 * 60 * 11,
                    },
                    {
                        id: "preview-1-assistant",
                        role: "assistant",
                        content: previewAssistantAnswer,
                        createdAt: now - 1000 * 60 * 8,
                    },
                ],
                "preview-conversation-2": [
                    {
                        id: "preview-2-user",
                        role: "user",
                        content: "Can you check my order status?",
                        createdAt: now - 1000 * 60 * 110,
                    },
                    {
                        id: "preview-2-assistant",
                        role: "assistant",
                        content: "Your order is in transit and expected tomorrow.",
                        createdAt: now - 1000 * 60 * 95,
                    },
                ],
                "preview-conversation-3": [
                    {
                        id: "preview-3-user",
                        role: "user",
                        content: "What is your refund window?",
                        createdAt: now - 1000 * 60 * 60 * 12,
                    },
                    {
                        id: "preview-3-assistant",
                        role: "assistant",
                        content: "You can request a refund within 30 days of delivery.",
                        createdAt: now - 1000 * 60 * 60 * 11,
                    },
                ],
            };
            const previewWelcome = (_b = options.previewConfiguration) === null || _b === void 0 ? void 0 : _b.welcome_message;
            const previewView = (_c = options.previewConfiguration) === null || _c === void 0 ? void 0 : _c.preview_view;
            const isClosedPreview = previewView === "closed";
            const normalizedPreviewView = previewView === "chat" ? "chat" : "home";
            setConfiguration((_d = options.previewConfiguration) !== null && _d !== void 0 ? _d : null);
            setAuthState("authorized");
            setAuthError(null);
            setIsOpen(!isClosedPreview);
            setView(isClosedPreview ? "home" : normalizedPreviewView);
            setFlowsDismissed(false);
            setFlowCardVisible(isClosedPreview);
            setPendingConversationId(null);
            setActiveConversationId(null);
            setConversations(previewConversations);
            setConversationMessages(previewMessagesByConversation);
            setFlowMessages(buildPreviewChatMessages(t, resolvedAgentName, previewWelcome, now));
            setFlowState(defaultFlowState);
            setFlowBadgesDismissed(false);
            setNextMessageSuggestions([]);
            return;
        }
        if (typeof window === "undefined")
            return;
        const customerStorageKey = options.testWidget ? `${CUSTOMER_ID_KEY}-test-${options.projectId}` : CUSTOMER_ID_KEY;
        const existing = window.localStorage.getItem(customerStorageKey);
        if (existing) {
            setCustomerId(existing);
            return;
        }
        const generated = generateUuid();
        try {
            window.localStorage.setItem(customerStorageKey, generated);
        }
        catch {
            // ignore storage exceptions
        }
        setCustomerId(generated);
    }, []);
    useEffect(() => {
        if (isPreview)
            return;
        if (typeof window === "undefined")
            return;
        if (!customerId)
            return;
        let cancelled = false;
        const authenticate = async () => {
            var _a, _b, _c, _d, _e, _f;
            setAuthState("loading");
            setAuthError(null);
            setAuthMessage(null);
            setAccessMode("active");
            setAccessMessageOnSend(null);
            try {
                const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/chatbot-widget-auth`;
                const payload = {
                    project_id: options.projectId,
                    publicKey: options.publicKey,
                    origin: window.location.origin,
                    customer_id: customerId,
                    test_widget: options.testWidget === true,
                    engine: options.engine,
                };
                if (options.language) {
                    payload.language = options.language;
                }
                if (options.additionalData && typeof options.additionalData === "object") {
                    payload.additional_data = options.additionalData;
                }
                const response = await fetch(endpoint, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(options.authToken ? { Authorization: `Bearer ${options.authToken}` } : {}),
                    },
                    body: JSON.stringify(payload),
                });
                const data = await response.json();
                if (cancelled)
                    return;
                if (!response.ok || !(data === null || data === void 0 ? void 0 : data.authorized)) {
                    const reason = typeof (data === null || data === void 0 ? void 0 : data.reason) === "string" ? data.reason : "";
                    const messageKey = typeof (data === null || data === void 0 ? void 0 : data.message_key) === "string" ? data.message_key : "";
                    const billingReasons = new Set([
                        "trial_required",
                        "trial_expired",
                        "subscription_ended",
                        "subscription_canceled",
                        "payment_required",
                        "subscription_incomplete",
                        "insufficient_credits",
                        "subscription_inactive",
                    ]);
                    const billingMessageKeys = new Set([
                        "TRIAL_REQUIRED_TO_GO_LIVE",
                        "TRIAL_EXPIRED",
                        "SUBSCRIPTION_ENDED",
                        "SUBSCRIPTION_CANCELED",
                        "PAYMENT_REQUIRED",
                        "SUBSCRIPTION_INCOMPLETE",
                        "INSUFFICIENT_CREDITS",
                        "SUBSCRIPTION_INACTIVE",
                    ]);
                    const isBillingBlocked = billingReasons.has(reason) || billingMessageKeys.has(messageKey);
                    if (isBillingBlocked) {
                        setConfiguration((_a = data === null || data === void 0 ? void 0 : data.configuration) !== null && _a !== void 0 ? _a : null);
                        setAuthState("blocked");
                        setAuthMessage((_c = (_b = data === null || data === void 0 ? void 0 : data.message) !== null && _b !== void 0 ? _b : data === null || data === void 0 ? void 0 : data.detail) !== null && _c !== void 0 ? _c : t("widget.auth.startTrialActivate"));
                        return;
                    }
                    setAuthState("unauthorized");
                    setAuthError((_d = data === null || data === void 0 ? void 0 : data.detail) !== null && _d !== void 0 ? _d : t("widget.auth.unauthorized"));
                    return;
                }
                setConfiguration((_e = data.configuration) !== null && _e !== void 0 ? _e : null);
                setConversationEngine(data.engine === "v2" ? "v2" : "v1");
                setV2Session(typeof data.v2_session === "string" ? data.v2_session : null);
                const access = (_f = data === null || data === void 0 ? void 0 : data.access) !== null && _f !== void 0 ? _f : null;
                const nextMode = (access === null || access === void 0 ? void 0 : access.mode) === "trial_inactive" ? "trial_inactive" : "active";
                setAccessMode(nextMode);
                setAccessMessageOnSend(typeof (access === null || access === void 0 ? void 0 : access.message_on_send) === "string" && access.message_on_send.trim()
                    ? access.message_on_send.trim()
                    : null);
                setAuthState("authorized");
            }
            catch (error) {
                console.error("Chatbot authentication failed", error);
                if (cancelled)
                    return;
                setAuthState("unauthorized");
                setAuthError(t("widget.auth.unableAuth"));
            }
        };
        authenticate();
        return () => {
            cancelled = true;
        };
    }, [baseUrl, customerId, options.additionalData, options.authToken, options.engine, options.testWidget, options.language, options.projectId, options.publicKey]);
    useEffect(() => {
        if (view !== "chat") {
            setFlowInfoOpen(false);
        }
    }, [view]);
    const appendConversationMessage = (conversationId, message) => {
        setConversationMessages((prev) => {
            const next = prev[conversationId] ? [...prev[conversationId], message] : [message];
            return { ...prev, [conversationId]: next };
        });
        setFlowMessages((prev) => {
            const currentId = activeConversationRef.current;
            if (currentId && currentId !== conversationId)
                return prev;
            return [...prev, message];
        });
        setConversations((prev) => {
            const nowIso = new Date(message.createdAt).toISOString();
            const existing = prev.find((item) => item.id === conversationId);
            if (!existing) {
                return [
                    {
                        id: conversationId,
                        customer: customerId !== null && customerId !== void 0 ? customerId : "",
                        name: t("widget.conversation.defaultShort"),
                        createdAt: nowIso,
                        lastMessageAt: nowIso,
                    },
                    ...prev,
                ];
            }
            return prev.map((item) => item.id === conversationId ? { ...item, lastMessageAt: nowIso } : item);
        });
    };
    const resetHandoffState = (conversationId) => {
        setHandoffActive(false);
        setHandoffToken(null);
        setHandoffExpiresAt(null);
        setHandoffStatus("idle");
        setHandoffAgent(null);
        setHandoffConversationId(conversationId !== null && conversationId !== void 0 ? conversationId : null);
        if (conversationId) {
            delete handoffIntroShownRef.current[conversationId];
        }
        if (handoffSocketRef.current) {
            handoffSocketRef.current.close();
            handoffSocketRef.current = null;
        }
    };
    const announceHandoff = (conversationId, agent) => {
        var _a, _b;
        if (handoffIntroShownRef.current[conversationId])
            return;
        handoffIntroShownRef.current[conversationId] = true;
        const label = (_a = agent === null || agent === void 0 ? void 0 : agent.username) !== null && _a !== void 0 ? _a : t("chat.support");
        appendConversationMessage(conversationId, {
            id: `handoff-intro-${Date.now()}`,
            role: "admin",
            content: "A human agent will join the chat shortly. Please wait a minute while they connect.",
            messageType: "handoff_connecting",
            createdAt: Date.now(),
            senderName: label,
            senderImage: (_b = agent === null || agent === void 0 ? void 0 : agent.image_url) !== null && _b !== void 0 ? _b : null,
        });
    };
    async function fetchConversations() {
        if (isPreview)
            return;
        if (options.testWidget)
            return;
        if (authState !== "authorized" || !customerId)
            return;
        try {
            const params = new URLSearchParams({ project_id: options.projectId });
            if (customerId)
                params.append("customer_id", customerId);
            const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/conversations?${params.toString()}`;
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error(`Failed to load conversations (${response.status})`);
            }
            const data = await response.json();
            const mapped = Array.isArray(data) ? data.map(mapConversationResponse) : [];
            mapped.forEach((item) => { v2EnginesRef.current[item.id] = item.engine || "v1"; });
            setConversations(mapped);
        }
        catch (error) {
            console.error("Failed to fetch conversations", error);
        }
    }
    async function fetchMessagesForConversation(conversationId, origin) {
        if (isPreview || !customerId)
            return;
        try {
            const params = new URLSearchParams({
                conversation_id: conversationId,
                project_id: options.projectId,
                customer_id: customerId,
            });
            const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/messages?${params.toString()}`;
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error(`Failed to load messages (${response.status})`);
            }
            const payload = await response.json();
            const data = Array.isArray(payload)
                ? payload
                : Array.isArray(payload === null || payload === void 0 ? void 0 : payload.results)
                    ? payload.results
                    : [];
            const mapped = data
                .map(mapConversationMessage)
                .sort((a, b) => a.createdAt - b.createdAt);
            if (data.some((item) => { var _a; return ((_a = item.response_payload) === null || _a === void 0 ? void 0 : _a.engine) === "v2"; }))
                v2EnginesRef.current[conversationId] = "v2";
            setConversationMessages((prev) => ({ ...prev, [conversationId]: mapped }));
            const latestActiveId = activeConversationRef.current;
            setFlowMessages((prev) => {
                const currentId = activeConversationRef.current;
                if (currentId && currentId !== conversationId)
                    return prev;
                return mapped;
            });
            if (latestActiveId && latestActiveId !== conversationId)
                return;
            setActiveConversationId(conversationId);
            setNextMessageSuggestions(getLastAssistantSuggestions(mapped));
            if (origin)
                setLastEntryTab(origin);
        }
        catch (error) {
            console.error("Failed to fetch conversation messages", error);
        }
    }
    const connectToHandoff = (conversationId, token) => {
        if (typeof window === "undefined")
            return;
        if (handoffSocketRef.current) {
            handoffSocketRef.current.close();
            handoffSocketRef.current = null;
        }
        setHandoffStatus("connecting");
        try {
            const socket = new WebSocket(buildHandoffWebsocketUrl(baseUrl, conversationId, token));
            handoffSocketRef.current = socket;
            socket.onopen = () => {
                setHandoffStatus("connected");
                announceHandoff(conversationId, handoffAgent);
            };
            socket.onmessage = (event) => {
                var _a, _b, _c, _d;
                try {
                    const data = JSON.parse(event.data || "{}");
                    if ((data === null || data === void 0 ? void 0 : data.type) !== "message")
                        return;
                    if ((data === null || data === void 0 ? void 0 : data.role) !== "admin")
                        return;
                    const sender = (_a = data === null || data === void 0 ? void 0 : data.sent_by_subaccount) !== null && _a !== void 0 ? _a : null;
                    const nextAgent = sender
                        ? { id: sender.id, username: sender.username, image_url: (_b = sender.image_url) !== null && _b !== void 0 ? _b : null }
                        : null;
                    if ((nextAgent === null || nextAgent === void 0 ? void 0 : nextAgent.username) || (nextAgent === null || nextAgent === void 0 ? void 0 : nextAgent.image_url)) {
                        setHandoffAgent(nextAgent);
                    }
                    const message = {
                        id: `handoff-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        role: (data === null || data === void 0 ? void 0 : data.role) === "admin" ? "admin" : "assistant",
                        content: (_c = data === null || data === void 0 ? void 0 : data.content) !== null && _c !== void 0 ? _c : "",
                        createdAt: parseDateToMs(data === null || data === void 0 ? void 0 : data.created_at),
                        senderName: sender === null || sender === void 0 ? void 0 : sender.username,
                        senderImage: (_d = sender === null || sender === void 0 ? void 0 : sender.image_url) !== null && _d !== void 0 ? _d : null,
                    };
                    appendConversationMessage(conversationId, message);
                }
                catch (error) {
                    console.error("Failed to parse handoff message", error);
                }
            };
            socket.onclose = () => {
                setHandoffStatus("idle");
            };
            socket.onerror = () => {
                setHandoffStatus("idle");
            };
        }
        catch (error) {
            console.error("Failed to open handoff socket", error);
            setHandoffStatus("idle");
        }
    };
    const sendHandoffMessage = (conversationId, message) => {
        const socket = handoffSocketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            return false;
        }
        try {
            socket.send(JSON.stringify({ message }));
            appendConversationMessage(conversationId, {
                id: `user-${Date.now()}`,
                role: "user",
                content: message,
                createdAt: Date.now(),
            });
            return true;
        }
        catch (error) {
            console.error("Failed to send handoff message", error);
            return false;
        }
    };
    const fetchHandoffStatus = async (conversationId) => {
        var _a, _b, _c, _d;
        if (!customerId)
            return;
        try {
            const params = new URLSearchParams({
                project_id: options.projectId,
                customer_id: customerId,
            });
            const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/conversations/${conversationId}/handoff?${params.toString()}`;
            const response = await fetch(endpoint);
            if (!response.ok) {
                if (response.status === 400 || response.status === 404) {
                    resetHandoffState(conversationId);
                    return;
                }
                throw new Error(`Failed to fetch handoff (${response.status})`);
            }
            const data = await response.json();
            const isActive = Boolean(data === null || data === void 0 ? void 0 : data.handoff_active);
            const token = (_a = data === null || data === void 0 ? void 0 : data.handoff_token) !== null && _a !== void 0 ? _a : null;
            const expiresAt = (_b = data === null || data === void 0 ? void 0 : data.handoff_expires_at) !== null && _b !== void 0 ? _b : null;
            const connectedUser = (_c = data === null || data === void 0 ? void 0 : data.handoff_connected_user) !== null && _c !== void 0 ? _c : null;
            const connectedAgent = connectedUser
                ? {
                    id: connectedUser.id,
                    username: connectedUser.username,
                    image_url: (_d = connectedUser.image_url) !== null && _d !== void 0 ? _d : null,
                }
                : null;
            if (!isActive || !token) {
                resetHandoffState(conversationId);
                return;
            }
            if (connectedAgent) {
                setHandoffAgent(connectedAgent);
                announceHandoff(conversationId, connectedAgent);
            }
            setHandoffConversationId(conversationId);
            setHandoffActive(true);
            setHandoffToken(token);
            setHandoffExpiresAt(expiresAt);
            if (handoffStatus !== "connected" || handoffToken !== token) {
                connectToHandoff(conversationId, token);
            }
        }
        catch (error) {
            console.error("Failed to fetch handoff status", error);
        }
    };
    async function renameConversation(conversationId, name) {
        if (isPreview || !customerId || !conversationId)
            return;
        if (conversationId.startsWith("pending-")) {
            setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? { ...conv, name } : conv)));
            return;
        }
        const params = new URLSearchParams({ project_id: options.projectId, customer_id: customerId });
        const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/conversations/${conversationId}/?${params.toString()}`;
        try {
            const response = await fetch(endpoint, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name }),
            });
            if (!response.ok) {
                throw new Error(`Failed to rename conversation (${response.status})`);
            }
            const data = await response.json();
            const updated = mapConversationResponse(data);
            setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? updated : conv)));
        }
        catch (error) {
            console.error("Failed to rename conversation", error);
        }
    }
    async function deleteConversation(conversationId) {
        if (isPreview || !customerId || !conversationId)
            return;
        setConversations((prev) => prev.filter((conv) => conv.id !== conversationId));
        setConversationMessages((prev) => {
            const next = { ...prev };
            delete next[conversationId];
            return next;
        });
        setFlowStatesByConversation((prev) => {
            if (!prev[conversationId])
                return prev;
            const next = { ...prev };
            delete next[conversationId];
            return next;
        });
        if (conversationId.startsWith("pending-"))
            return;
        const params = new URLSearchParams({ project_id: options.projectId, customer_id: customerId });
        const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/conversations/${conversationId}?${params.toString()}`;
        try {
            const response = await fetch(endpoint, { method: "DELETE" });
            if (!response.ok && response.status !== 404) {
                throw new Error(`Failed to delete conversation (${response.status})`);
            }
        }
        catch (error) {
            console.error("Failed to delete conversation", error);
        }
    }
    useEffect(() => {
        if (isPreview || options.testWidget || authState !== "authorized" || !customerId)
            return;
        fetchConversations();
    }, [authState, customerId, baseUrl, options.projectId, options.testWidget]);
    const latestKnownConversationId = useMemo(() => {
        var _a;
        if (activeConversationId && !activeConversationId.startsWith("pending-"))
            return activeConversationId;
        if (flowState.conversationId && !flowState.conversationId.startsWith("pending-"))
            return flowState.conversationId;
        const latest = history.find((item) => !item.id.startsWith("pending-"));
        return (_a = latest === null || latest === void 0 ? void 0 : latest.id) !== null && _a !== void 0 ? _a : null;
    }, [activeConversationId, flowState.conversationId, history]);
    useEffect(() => {
        if (isPreview || authState !== "authorized" || !customerId || !latestKnownConversationId) {
            resetHandoffState(latestKnownConversationId !== null && latestKnownConversationId !== void 0 ? latestKnownConversationId : undefined);
            return;
        }
        let cancelled = false;
        const poll = async () => {
            if (cancelled)
                return;
            await fetchHandoffStatus(latestKnownConversationId);
        };
        poll();
        if (typeof window === "undefined")
            return;
        const timer = window.setInterval(poll, 12000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [authState, baseUrl, customerId, isPreview, latestKnownConversationId, options.projectId]);
    useEffect(() => {
        if (!latestKnownConversationId)
            return;
        if (handoffConversationId && handoffConversationId !== latestKnownConversationId) {
            resetHandoffState(latestKnownConversationId);
        }
    }, [handoffConversationId, latestKnownConversationId]);
    function startNewFlowConversation() {
        setFlowInput("");
        setFlowStreamingId(null);
        setFlowLoading(false);
        setFlowPendingPrompt(null);
        setFlowInfoOpen(false);
        setActiveConversationId(null);
        setPendingConversationId(null);
        if (isPreview) {
            setFlowMessages(buildPreviewChatMessages(t, resolvedAgentName, welcomeMessageText));
        }
        else {
            setFlowMessages([createWelcomeMessage(t, resolvedAgentName, welcomeMessageText)]);
        }
        setFlowState(defaultFlowState);
        setFlowBadgesDismissed(false);
        setNextMessageSuggestions([]);
        setLastEntryTab("home");
        setView("chat");
        setIsOpen(true);
    }
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        if (isPreview || typeof window === "undefined")
            return;
        let restored = false;
        const stored = window.sessionStorage.getItem(flowSessionKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed.flowMessages) && parsed.flowMessages.length) {
                    setFlowMessages(parsed.flowMessages);
                }
                if (parsed.flowState)
                    setFlowState({ ...defaultFlowState, ...parsed.flowState });
                if (parsed.flowStatesByConversation)
                    setFlowStatesByConversation(parsed.flowStatesByConversation);
                if (Array.isArray(parsed.conversations))
                    setConversations(parsed.conversations);
                if (parsed.conversationMessages)
                    setConversationMessages(parsed.conversationMessages);
                if (parsed.conversationEngines)
                    v2EnginesRef.current = parsed.conversationEngines;
                if (parsed.activeConversationId)
                    setActiveConversationId(parsed.activeConversationId);
                if (parsed.pendingConversationId)
                    setPendingConversationId(parsed.pendingConversationId);
                if (typeof parsed.isOpen === "boolean")
                    setIsOpen(parsed.isOpen);
                if (parsed.view)
                    setView(parsed.view);
                if (parsed.lastEntryTab)
                    setLastEntryTab(parsed.lastEntryTab);
                if (parsed.flowsDismissed || parsed.suggestionsDismissed)
                    setFlowsDismissed(true);
                if ((_a = parsed.history) === null || _a === void 0 ? void 0 : _a.length)
                    setHistory(parsed.history);
                setNextMessageSuggestions(getRestoredSuggestionsFromSessionState(parsed));
                restored = true;
            }
            catch (error) {
                console.warn("Failed to restore flow session state", error);
            }
        }
        if (!restored && options.projectId && !options.testWidget) {
            const legacy = window.localStorage.getItem(FLOW_LEGACY_KEY);
            if (legacy) {
                try {
                    const parsed = JSON.parse(legacy || "{}");
                    const entry = parsed === null || parsed === void 0 ? void 0 : parsed[options.projectId];
                    if (entry) {
                        const nextMessages = Array.isArray(entry.messages) ? entry.messages : null;
                        if (nextMessages && nextMessages.length) {
                            setFlowMessages(nextMessages);
                        }
                        const nextFlowState = {
                            ...defaultFlowState,
                            conversationId: (_c = (_b = entry.conversationId) !== null && _b !== void 0 ? _b : entry.conversation_id) !== null && _c !== void 0 ? _c : null,
                            flowRunId: (_e = (_d = entry.flowRunId) !== null && _d !== void 0 ? _d : entry.flow_run_id) !== null && _e !== void 0 ? _e : null,
                            bookingFlowRunId: (_g = (_f = entry.bookingFlowRunId) !== null && _f !== void 0 ? _f : entry.booking_flow_run_id) !== null && _g !== void 0 ? _g : null,
                            selectedFlowId: (_j = (_h = entry.selectedFlowId) !== null && _h !== void 0 ? _h : entry.selected_flow_id) !== null && _j !== void 0 ? _j : null,
                            selectedPlaybookId: (_m = (_l = (_k = entry.selectedPlaybookId) !== null && _k !== void 0 ? _k : entry.selected_playbook_id) !== null && _l !== void 0 ? _l : entry.playbook_id) !== null && _m !== void 0 ? _m : null,
                            currentStepId: (_q = (_p = (_o = entry.currentStepId) !== null && _o !== void 0 ? _o : entry.current_step_id) !== null && _p !== void 0 ? _p : entry.step_id) !== null && _q !== void 0 ? _q : null,
                            flowStatus: (_s = (_r = entry.flowStatus) !== null && _r !== void 0 ? _r : entry.flow_status) !== null && _s !== void 0 ? _s : null,
                            flowIsComplete: typeof entry.flowIsComplete === "boolean" ? entry.flowIsComplete : null,
                            flowCompletionPercent: typeof entry.flowCompletionPercent === "number" ? entry.flowCompletionPercent : null,
                            flowMissingFields: Array.isArray(entry.flowMissingFields) ? entry.flowMissingFields : [],
                            flowValidationIssues: Array.isArray(entry.flowValidationIssues) ? entry.flowValidationIssues : [],
                            rawCompletion: typeof entry.rawCompletion === "string" ? entry.rawCompletion : null,
                            lastUpdated: typeof entry.lastUpdated === "number" ? entry.lastUpdated : null,
                        };
                        setFlowState(nextFlowState);
                        if (nextFlowState.conversationId) {
                            const nowIso = new Date().toISOString();
                            setConversations([
                                {
                                    id: nextFlowState.conversationId,
                                    customer: "",
                                    name: t("widget.conversation.new"),
                                    createdAt: nowIso,
                                    lastMessageAt: nowIso,
                                },
                            ]);
                            if (nextMessages) {
                                setConversationMessages({ [nextFlowState.conversationId]: nextMessages });
                            }
                            setActiveConversationId(nextFlowState.conversationId);
                            setFlowStatesByConversation({ [nextFlowState.conversationId]: nextFlowState });
                        }
                    }
                }
                catch (error) {
                    console.warn("Failed to restore legacy flow chat state", error);
                }
            }
        }
        const media = window.matchMedia("(max-width: 640px)");
        const syncMobile = (event) => setIsMobile(event ? event.matches : media.matches);
        syncMobile();
        media.addEventListener("change", syncMobile);
        return () => media.removeEventListener("change", syncMobile);
    }, [isPreview, options.floating, options.previewConfiguration, options.projectId]);
    useEffect(() => {
        widgetUiStateRef.current = { isOpen, view, lastEntryTab };
    }, [isOpen, lastEntryTab, view]);
    useEffect(() => {
        var _a, _b;
        if (typeof window === "undefined")
            return;
        const syncViewport = () => {
            var _a, _b, _c, _d;
            const nextHeight = Math.round((_b = (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : window.innerHeight);
            const nextOffsetTop = Math.max(0, Math.round((_d = (_c = window.visualViewport) === null || _c === void 0 ? void 0 : _c.offsetTop) !== null && _d !== void 0 ? _d : 0));
            setMobileViewportHeight((prev) => (prev === nextHeight ? prev : nextHeight));
            setMobileViewportOffsetTop((prev) => (prev === nextOffsetTop ? prev : nextOffsetTop));
        };
        syncViewport();
        window.addEventListener("resize", syncViewport);
        window.addEventListener("orientationchange", syncViewport);
        (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.addEventListener("resize", syncViewport);
        (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.addEventListener("scroll", syncViewport);
        return () => {
            var _a, _b;
            window.removeEventListener("resize", syncViewport);
            window.removeEventListener("orientationchange", syncViewport);
            (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.removeEventListener("resize", syncViewport);
            (_b = window.visualViewport) === null || _b === void 0 ? void 0 : _b.removeEventListener("scroll", syncViewport);
        };
    }, []);
    useEffect(() => {
        if (isPreview || typeof window === "undefined" || !options.floating)
            return;
        const syncFromHistory = (state) => {
            if (state) {
                setIsOpen(true);
                setLastEntryTab(state.lastEntryTab);
                setView(state.mode === "chat" ? "chat" : state.view);
                setSettingsOpen(false);
                setFlowInfoOpen(false);
                return;
            }
            const current = widgetUiStateRef.current;
            if (!current.isOpen)
                return;
            setSettingsOpen(false);
            setFlowInfoOpen(false);
            setIsOpen(false);
        };
        const onPopState = (event) => {
            const state = isWidgetHistoryState(event.state) && event.state.sessionId === historySessionIdRef.current
                ? event.state
                : null;
            syncFromHistory(state);
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [isPreview, options.floating]);
    useEffect(() => {
        if (isPreview || typeof window === "undefined" || !options.floating)
            return;
        const sessionId = historySessionIdRef.current;
        const currentState = isWidgetHistoryState(window.history.state) && window.history.state.sessionId === sessionId
            ? window.history.state
            : null;
        const openView = normalizeOpenTab(view, normalizeOpenTab(lastEntryTab));
        const openState = {
            __noaWidget: true,
            sessionId,
            mode: "open",
            view: openView,
            lastEntryTab: openView,
        };
        const chatState = {
            __noaWidget: true,
            sessionId,
            mode: "chat",
            view: openView,
            lastEntryTab: openView,
        };
        if (!isOpen)
            return;
        if (view === "chat") {
            if (!currentState) {
                window.history.pushState(openState, "");
                window.history.pushState(chatState, "");
                return;
            }
            if (currentState.mode === "open") {
                window.history.pushState(chatState, "");
                return;
            }
            if (currentState.view !== chatState.view ||
                currentState.lastEntryTab !== chatState.lastEntryTab) {
                window.history.replaceState(chatState, "");
            }
            return;
        }
        if (!currentState) {
            window.history.pushState(openState, "");
            return;
        }
        if (currentState.mode === "open") {
            if (currentState.view !== openState.view || currentState.lastEntryTab !== openState.lastEntryTab) {
                window.history.replaceState(openState, "");
            }
        }
    }, [isOpen, isPreview, lastEntryTab, options.floating, view]);
    useEffect(() => {
        if (isPreview || typeof window === "undefined")
            return;
        const state = {
            isOpen,
            view,
            flowMessages,
            conversationEngines: v2EnginesRef.current,
            flowState,
            flowStatesByConversation,
            lastEntryTab,
            flowsDismissed,
            history,
            conversations,
            conversationMessages,
            nextMessageSuggestions,
            activeConversationId,
            pendingConversationId,
        };
        window.sessionStorage.setItem(flowSessionKey, JSON.stringify(state));
    }, [
        activeConversationId,
        conversations,
        conversationMessages,
        flowMessages,
        flowState,
        flowStatesByConversation,
        history,
        isOpen,
        isPreview,
        lastEntryTab,
        nextMessageSuggestions,
        pendingConversationId,
        flowsDismissed,
        view,
    ]);
    useEffect(() => {
        if (isPreview)
            return;
        if (!isOpen || flowsDismissed)
            return;
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem(flowCardKey, "true");
            window.sessionStorage.setItem(flowCardLegacyKey, "true");
        }
        setFlowsDismissed(true);
    }, [isOpen, flowsDismissed, isPreview]);
    useEffect(() => {
        if (!activeConversationId)
            return;
        setConversationMessages((prev) => {
            const existing = prev[activeConversationId];
            if (existing === flowMessages)
                return prev;
            return { ...prev, [activeConversationId]: flowMessages };
        });
    }, [activeConversationId, flowMessages]);
    useEffect(() => {
        activeConversationRef.current = activeConversationId;
    }, [activeConversationId]);
    useEffect(() => {
        return () => {
            if (handoffSocketRef.current) {
                handoffSocketRef.current.close();
                handoffSocketRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        if (!activeConversationId)
            return;
        setFlowStatesByConversation((prev) => ({
            ...prev,
            [activeConversationId]: { ...flowState, conversationId: activeConversationId },
        }));
    }, [activeConversationId, flowState]);
    useEffect(() => {
        if (!conversations.length) {
            setHistory([]);
            return;
        }
        const sorted = [...conversations].sort((a, b) => { var _a, _b; return parseDateToMs((_a = b.lastMessageAt) !== null && _a !== void 0 ? _a : b.createdAt) - parseDateToMs((_b = a.lastMessageAt) !== null && _b !== void 0 ? _b : a.createdAt); });
        const mapped = sorted.map((conversation) => {
            var _a, _b;
            const cached = conversationMessages[conversation.id];
            const lastMessage = cached === null || cached === void 0 ? void 0 : cached[cached.length - 1];
            const isPending = conversation.id.startsWith("pending-");
            const rawExcerpt = (_a = lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.content) !== null && _a !== void 0 ? _a : t("widget.conversation.openHistory");
            return {
                id: conversation.id,
                title: conversationTitle(conversation, t),
                timestamp: formatTimestampLabel((_b = conversation.lastMessageAt) !== null && _b !== void 0 ? _b : conversation.createdAt, t("widget.time.justNow")),
                excerpt: isPending ? t("widget.conversation.pending") : truncateText(rawExcerpt, 96),
            };
        });
        setHistory(mapped);
    }, [conversations, conversationMessages]);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        if (isOpen || flowsDismissed || !showFlowsWhenClosed || flowBadges.length === 0) {
            setFlowCardVisible(false);
            return;
        }
        const delaySeconds = isPreview ? 0 : widgetSettings.closedFlowsPopupDelaySeconds;
        const delay = Math.max(0, delaySeconds) * 1000;
        const introTimer = window.setTimeout(() => {
            setFlowCardVisible(true);
            if (soundEnabled) {
                const played = playNotificationSound();
                if (!played)
                    pendingNotificationSoundRef.current = true;
            }
        }, delay);
        return () => window.clearTimeout(introTimer);
    }, [
        isOpen,
        flowsDismissed,
        showFlowsWhenClosed,
        flowBadges.length,
        isPreview,
        playNotificationSound,
        soundEnabled,
        widgetSettings.closedFlowsPopupDelaySeconds,
    ]);
    function dismissClosedFlows() {
        setFlowCardVisible(false);
        if (isPreview)
            return;
        if (flowsDismissed)
            return;
        setFlowsDismissed(true);
        if (typeof window !== "undefined") {
            window.sessionStorage.setItem(flowCardKey, "true");
            window.sessionStorage.setItem(flowCardLegacyKey, "true");
        }
    }
    const isBlocked = authState === "blocked";
    const isChatting = view === "chat" && !isBlocked;
    const activeConversationKey = (_a = activeConversationId !== null && activeConversationId !== void 0 ? activeConversationId : flowState.conversationId) !== null && _a !== void 0 ? _a : null;
    const isHandoffConnected = handoffActive && handoffStatus === "connected" && handoffConversationId === activeConversationKey;
    const handoffInputBlocked = handoffActive && handoffConversationId === activeConversationKey && handoffStatus !== "connected";
    const flowInputDisabled = isPreview ||
        flowLoading ||
        !!flowStreamingId ||
        (!consent.approved && isChatting) ||
        authState !== "authorized" ||
        !customerId ||
        handoffInputBlocked;
    function handleFlowSend(prompt, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (isPreview)
            return;
        lastFailedFlowRequestRef.current = null;
        const bypassConsent = bypassConsentRef.current;
        if (bypassConsent) {
            bypassConsentRef.current = false;
        }
        const pickedSuggestion = typeof prompt === "object" && prompt !== null
            ? prompt
            : prompt === undefined && draftSuggestionRef.current
                ? { ...draftSuggestionRef.current, message: flowInput }
                : null;
        const rawValue = pickedSuggestion ? pickedSuggestion.message : typeof prompt === "string" ? prompt : flowInput;
        const value = rawValue.trim();
        if (!value)
            return;
        const lastSend = flowLastSendRef.current;
        const now = Date.now();
        if (lastSend && lastSend.value === value && now - lastSend.at < 1200) {
            return;
        }
        flowLastSendRef.current = { value, at: now };
        if (flowInputDisabled && !bypassConsent)
            return;
        const targetConversationId = (_a = activeConversationId !== null && activeConversationId !== void 0 ? activeConversationId : flowState.conversationId) !== null && _a !== void 0 ? _a : null;
        const isHandoffConversation = Boolean(targetConversationId) && handoffActive && handoffConversationId === targetConversationId;
        const shouldUseHandoff = isHandoffConversation && handoffStatus === "connected";
        dismissClosedFlows();
        setFlowBadgesDismissed(true);
        setNextMessageSuggestions([]);
        setView("chat");
        setIsOpen(true);
        const forceNewConversation = (_b = options === null || options === void 0 ? void 0 : options.forceNewConversation) !== null && _b !== void 0 ? _b : false;
        const selectedFlowId = (_f = (_e = (_c = options === null || options === void 0 ? void 0 : options.selectedFlowId) !== null && _c !== void 0 ? _c : (pickedSuggestion && "selectedFlowId" in pickedSuggestion ? (_d = pickedSuggestion.selectedFlowId) !== null && _d !== void 0 ? _d : null : null)) !== null && _e !== void 0 ? _e : flowState.selectedFlowId) !== null && _f !== void 0 ? _f : null;
        const flowRunId = (_h = (_g = options === null || options === void 0 ? void 0 : options.flowRunId) !== null && _g !== void 0 ? _g : flowState.flowRunId) !== null && _h !== void 0 ? _h : null;
        const selectedPlaybookId = options && "selectedPlaybookId" in options
            ? (_j = options.selectedPlaybookId) !== null && _j !== void 0 ? _j : null
            : pickedSuggestion && "selectedPlaybookId" in pickedSuggestion
                ? (_k = pickedSuggestion.selectedPlaybookId) !== null && _k !== void 0 ? _k : null
                : flowState.selectedPlaybookId;
        const currentStepId = options && "currentStepId" in options
            ? (_l = options.currentStepId) !== null && _l !== void 0 ? _l : null
            : pickedSuggestion && "currentStepId" in pickedSuggestion
                ? (_m = pickedSuggestion.currentStepId) !== null && _m !== void 0 ? _m : null
                : flowState.currentStepId;
        const clickedSuggestion = Boolean((options === null || options === void 0 ? void 0 : options.clickedSuggestion) || pickedSuggestion);
        if (isHandoffConversation && !shouldUseHandoff) {
            return;
        }
        if (shouldUseHandoff && targetConversationId) {
            if (!consent.approved && !bypassConsent) {
                setFlowPendingPrompt({
                    message: value,
                    selectedFlowId,
                    flowRunId,
                    forceNewConversation,
                    selectedPlaybookId,
                    currentStepId,
                    clickedSuggestion,
                });
                return;
            }
            setFlowInput("");
            sendHandoffMessage(targetConversationId, value);
            return;
        }
        if (forceNewConversation) {
            setActiveConversationId(null);
            setPendingConversationId(null);
            setFlowMessages([createWelcomeMessage(t, resolvedAgentName, welcomeMessageText)]);
            setFlowState({ ...defaultFlowState, selectedFlowId, flowRunId, selectedPlaybookId, currentStepId });
        }
        else if (options === null || options === void 0 ? void 0 : options.selectedFlowId) {
            setFlowState((prev) => ({ ...prev, selectedFlowId, flowRunId, selectedPlaybookId, currentStepId }));
        }
        else if (pickedSuggestion || (options === null || options === void 0 ? void 0 : options.selectedPlaybookId) !== undefined || (options === null || options === void 0 ? void 0 : options.currentStepId) !== undefined) {
            setFlowState((prev) => ({ ...prev, selectedFlowId, flowRunId, selectedPlaybookId, currentStepId }));
        }
        let tempConversationId = forceNewConversation ? null : pendingConversationId;
        if (forceNewConversation || !activeConversationId) {
            const ensuredTempConversationId = tempConversationId !== null && tempConversationId !== void 0 ? tempConversationId : `pending-${Date.now()}`;
            tempConversationId = ensuredTempConversationId;
            setPendingConversationId(ensuredTempConversationId);
            const nowIso = new Date().toISOString();
            setConversations((prev) => {
                const filtered = prev.filter((item) => item.id !== ensuredTempConversationId);
                return [
                    {
                        id: ensuredTempConversationId,
                        customer: customerId !== null && customerId !== void 0 ? customerId : "",
                        name: t("widget.conversation.new"),
                        createdAt: nowIso,
                        lastMessageAt: nowIso,
                    },
                    ...filtered,
                ];
            });
        }
        if (!consent.approved && !bypassConsent) {
            setFlowPendingPrompt({
                message: value,
                selectedFlowId,
                flowRunId,
                forceNewConversation,
                selectedPlaybookId,
                currentStepId,
                clickedSuggestion,
            });
            return;
        }
        setFlowInput("");
        const userMessage = { id: `user-${Date.now()}`, role: "user", content: value, createdAt: Date.now() };
        if (flowSendLockRef.current)
            return;
        flowSendLockRef.current = true;
        setFlowMessages((prev) => [...prev, userMessage]);
        setFlowLoading(true);
        const sendOptions = forceNewConversation
            ? { selectedFlowId, flowRunId, selectedPlaybookId, currentStepId, clickedSuggestion, conversationId: null }
            : { selectedFlowId, flowRunId, selectedPlaybookId, currentStepId, clickedSuggestion };
        lastFlowRequestRef.current = { message: value, pendingId: tempConversationId, overrides: sendOptions };
        sendToFlowBackend(value, tempConversationId, sendOptions);
    }
    function handleRetryLastMessage() {
        if (flowInputDisabled || flowSendLockRef.current)
            return;
        const lastFailed = lastFailedFlowRequestRef.current;
        if (!lastFailed)
            return;
        setNextMessageSuggestions([]);
        setFlowLoading(true);
        flowSendLockRef.current = true;
        setView("chat");
        setIsOpen(true);
        sendToFlowBackend(lastFailed.message, lastFailed.pendingId, lastFailed.overrides);
    }
    function handleFlowBadgePick(flow, origin) {
        if (origin === "closed" || origin === "home") {
            setLastEntryTab("home");
        }
        setFlowBadgesDismissed(true);
        handleFlowSend(flow.label, { selectedFlowId: flow.id, forceNewConversation: true });
    }
    function streamAssistantMessage(answer, sources, products, applyMessages, applyStreamingId) {
        const text = answer || t("widget.errors.noResponse");
        const appendFinalMessage = () => applyMessages((prev) => [
            ...prev,
            { id: `assistant-${Date.now()}`, role: "assistant", content: text, sources, products, createdAt: Date.now() },
        ]);
        if (typeof window === "undefined") {
            appendFinalMessage();
            applyStreamingId(null);
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            const messageId = `assistant-${Date.now()}`;
            const createdAt = Date.now();
            applyStreamingId(messageId);
            applyMessages((prev) => [
                ...prev,
                { id: messageId, role: "assistant", content: "", sources, products, streaming: true, createdAt },
            ]);
            let slice = 0;
            const step = 3;
            const intervalMs = 20;
            const timer = window.setInterval(() => {
                slice = Math.min(text.length, slice + step);
                const nextContent = text.slice(0, slice);
                applyMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, content: nextContent } : m)));
                if (slice >= text.length) {
                    window.clearInterval(timer);
                    applyStreamingId(null);
                    applyMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, streaming: false, content: text, sources, products } : m)));
                    resolve();
                }
            }, intervalMs);
        });
    }
    async function confirmBookingSlot(messageId, slot) {
        var _a, _b, _c, _d;
        if (!options.projectId || !customerId)
            return;
        const bookingMessage = flowMessages.find((message) => message.id === messageId);
        const bookingConversation = flowState.conversationId || activeConversationId;
        if (bookingConversation && (v2EnginesRef.current[bookingConversation] || conversationEngine) === "v2" && (bookingMessage === null || bookingMessage === void 0 ? void 0 : bookingMessage.bookingFlowRunId)) {
            if (flowSendLockRef.current)
                return;
            setFlowLoading(true);
            flowSendLockRef.current = true;
            try {
                await sendToV2Backend(`${t("chat.confirmBooking")}: ${slot.start}`, bookingConversation, {
                    type: "booking.select", task_id: bookingMessage.bookingFlowRunId, start: slot.start, end: slot.end,
                });
            }
            catch {
                setFlowMessages((prev) => [...prev, { id: `booking-error-${Date.now()}`, role: "assistant", content: t("widget.errors.bookingConfirmFailed"), error: true, createdAt: Date.now() }]);
            }
            finally {
                setFlowLoading(false);
                flowSendLockRef.current = false;
            }
            return;
        }
        const flowRunId = (_b = (_a = bookingMessage === null || bookingMessage === void 0 ? void 0 : bookingMessage.bookingFlowRunId) !== null && _a !== void 0 ? _a : flowState.bookingFlowRunId) !== null && _b !== void 0 ? _b : flowState.flowRunId;
        if (!flowRunId) {
            setFlowMessages((prev) => [
                ...prev,
                {
                    id: `assistant-${Date.now()}`,
                    role: "assistant",
                    content: t("widget.errors.moreContext"),
                    error: true,
                    createdAt: Date.now(),
                },
            ]);
            return;
        }
        setFlowMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, bookingStatus: "pending" } : m)));
        const payload = {
            project_id: options.projectId,
            customer_id: customerId,
            conversation_id: flowState.conversationId || activeConversationId || undefined,
            flow_run_id: flowRunId,
            slot_start: slot.start,
            slot_end: slot.end,
        };
        if (!payload.conversation_id)
            delete payload.conversation_id;
        try {
            const base = normalizeBaseUrl(baseUrl);
            const primaryEndpoint = `${base}/chatbot/booking/confirm`;
            let response = await fetch(primaryEndpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok && response.status === 404) {
                const fallbackEndpoint = `${base}/chat/booking/confirm`;
                response = await fetch(fallbackEndpoint, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });
            }
            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }
            const data = await response.json();
            const redirectedFlowId = normalizeSupportId((_c = data.selected_flow_id) !== null && _c !== void 0 ? _c : data.redirect_on_completion_flow_id);
            const redirectedFlowRunId = normalizeSupportId((_d = data.flow_run_id) !== null && _d !== void 0 ? _d : data.next_flow_run);
            const conversationId = flowState.conversationId || activeConversationId || null;
            setFlowMessages((prev) => prev.map((m) => m.id === messageId
                ? {
                    ...m,
                    bookingStatus: "confirmed",
                    bookingDetails: {
                        start: data.start || slot.start,
                        end: data.end || slot.end,
                        eventId: data.event_id,
                    },
                }
                : m));
            setFlowMessages((prev) => [
                ...prev,
                {
                    id: `assistant-${Date.now()}`,
                    role: "assistant",
                    content: t("widget.errors.meetingBooked"),
                    createdAt: Date.now(),
                },
            ]);
            setFlowState((prev) => ({ ...prev, bookingFlowRunId: null }));
            if (redirectedFlowId || redirectedFlowRunId) {
                setFlowState((prev) => ({
                    ...prev,
                    conversationId: conversationId !== null && conversationId !== void 0 ? conversationId : prev.conversationId,
                    flowRunId: redirectedFlowRunId,
                    bookingFlowRunId: null,
                    selectedFlowId: redirectedFlowId,
                    selectedPlaybookId: null,
                    currentStepId: null,
                    flowStatus: "in_progress",
                    flowIsComplete: false,
                    flowCompletionPercent: 0,
                    flowMissingFields: [],
                    flowValidationIssues: [],
                    rawCompletion: null,
                    lastUpdated: Date.now(),
                }));
                setNextMessageSuggestions([]);
                setFlowLoading(true);
                flowSendLockRef.current = true;
                window.setTimeout(() => {
                    sendToFlowBackend(FLOW_REDIRECT_CONTINUE_MESSAGE, null, {
                        conversationId,
                        selectedFlowId: redirectedFlowId,
                        flowRunId: redirectedFlowRunId,
                        selectedPlaybookId: null,
                        currentStepId: null,
                    });
                }, 0);
            }
        }
        catch (error) {
            console.error("Booking confirmation failed", error);
            setFlowMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, bookingStatus: "failed" } : m)));
            setFlowMessages((prev) => [
                ...prev,
                {
                    id: `assistant-${Date.now()}`,
                    role: "assistant",
                    content: t("widget.errors.bookingConfirmFailed"),
                    error: true,
                    createdAt: Date.now(),
                },
            ]);
        }
    }
    async function receiveV2Run(run) {
        var _a;
        run = await resolveStreamReceipt(normalizeBaseUrl(baseUrl), run);
        let transport = v2TransportRef.current;
        if (!transport || transport.isClosed || transport.conversationId !== run.conversation_id) {
            transport === null || transport === void 0 ? void 0 : transport.close();
            transport = new ConversationTransport(normalizeBaseUrl(baseUrl), run.conversation_id, run.conversation_stream_token);
            v2TransportRef.current = transport;
        }
        transport.setDebug(options.testWidget && options.authToken ? {
            ownerToken: options.authToken,
            receive: (runId, debug) => { var _a; if (((_a = v2RunRef.current) === null || _a === void 0 ? void 0 : _a.run_id) === runId)
                setV2Debug(debug); },
        } : undefined);
        transport.setVisible(isOpen);
        v2RunRef.current = run;
        v2EnginesRef.current[run.conversation_id] = "v2";
        const assistantId = `v2-${run.run_id}`;
        let state = initialAgentState();
        setActiveConversationId(run.conversation_id);
        activeConversationRef.current = run.conversation_id;
        setPendingConversationId(null);
        setFlowState((prev) => ({ ...prev, conversationId: run.conversation_id }));
        setFlowStreamingId(null);
        const update = (event) => {
            var _a, _b, _c;
            state = reduceAgentEvent(state, event);
            const latest = ((_a = v2RunRef.current) === null || _a === void 0 ? void 0 : _a.run_id) === run.run_id;
            const visible = activeConversationRef.current === run.conversation_id;
            const terminal = ["completed", "failed", "interrupted"].includes(state.status);
            const next = {
                id: assistantId, role: "assistant", content: state.text || (state.error ? t("widget.errors.connectFailed") : ""),
                products: state.products, sources: state.sources, calendar: state.calendar,
                messageType: state.calendar ? "calendar" : undefined, bookingFlowRunId: state.bookingTaskId,
                bookingStatus: ((_b = state.booking) === null || _b === void 0 ? void 0 : _b.confirmed) ? "confirmed" : state.calendar ? "pending" : undefined,
                bookingDetails: state.booking ? { start: state.booking.start, end: state.booking.end } : undefined,
                messageSuggestions: state.suggestions.map((message) => ({ message })),
                streaming: !terminal, createdAt: Date.now(), error: !!state.error,
            };
            const merge = (messages) => {
                const found = messages.some((message) => message.id === assistantId);
                return found ? messages.map((message) => message.id === assistantId ? { ...message, ...next, createdAt: message.createdAt } : message)
                    : state.text || state.calendar || state.products || state.error ? [...messages, next] : messages;
            };
            setConversationMessages((prev) => ({ ...prev, [run.conversation_id]: merge(prev[run.conversation_id] || []) }));
            if (visible) {
                setFlowMessages(merge);
                if (latest) {
                    const activityKey = activityTranslationKey(state);
                    setV2Activity(activityKey ? state.activity === "products.searching" && state.activityGoal
                        ? t("chat.activityProductsFor", { goal: state.activityGoal }) : t(activityKey) : null);
                    setNextMessageSuggestions(state.suggestions.map((message) => ({ message })));
                    setFlowStreamingId(terminal || !state.text ? null : assistantId);
                }
            }
            if (((_c = state.handoff) === null || _c === void 0 ? void 0 : _c.active) && state.handoff.token && visible) {
                setHandoffConversationId(run.conversation_id);
                setHandoffActive(true);
                setHandoffToken(state.handoff.token);
                connectToHandoff(run.conversation_id, state.handoff.token);
            }
        };
        const subscription = transport.subscribe(run, update);
        try {
            await subscription.completed;
        }
        finally {
            if (((_a = v2RunRef.current) === null || _a === void 0 ? void 0 : _a.run_id) === run.run_id) {
                setV2Activity(null);
                setFlowStreamingId(null);
            }
        }
    }
    async function sendToV2Backend(message, conversationId, uiAction) {
        if (!v2Session)
            throw new Error("Widget session is unavailable.");
        setV2Activity(null);
        setV2Debug(null);
        const pending = v2RequestRef.current;
        const request = pending && pending.message === message && pending.conversationId === conversationId ? pending : { id: generateUuid(), message, conversationId, uiAction };
        v2RequestRef.current = request;
        try {
            window.sessionStorage.setItem(`noa-v2-request-${options.projectId}-${customerId}`, JSON.stringify(request));
        }
        catch { }
        try {
            const run = request.run || await startRun(normalizeBaseUrl(baseUrl), v2Session, options.authToken, {
                request_id: request.id, message, conversation_id: request.conversationId, language: activeLanguage,
                ui_action: request.uiAction,
            });
            request.run = run;
            try {
                window.sessionStorage.setItem(`noa-v2-run-${options.projectId}-${customerId}`, JSON.stringify(run));
            }
            catch { }
            await receiveV2Run(run);
            v2RequestRef.current = null;
            try {
                window.sessionStorage.removeItem(`noa-v2-run-${options.projectId}-${customerId}`);
                window.sessionStorage.removeItem(`noa-v2-request-${options.projectId}-${customerId}`);
            }
            catch { }
            lastFailedFlowRequestRef.current = null;
            if (!options.testWidget)
                void fetchConversations();
        }
        finally {
            setV2Activity(null);
        }
    }
    useEffect(() => () => {
        var _a;
        (_a = v2TransportRef.current) === null || _a === void 0 ? void 0 : _a.close();
        v2TransportRef.current = null;
    }, [v2Session, customerId, baseUrl, options.projectId]);
    useEffect(() => {
        var _a, _b;
        (_a = v2TransportRef.current) === null || _a === void 0 ? void 0 : _a.setVisible(isOpen);
        if (handoffActive && !flowLoading)
            (_b = v2TransportRef.current) === null || _b === void 0 ? void 0 : _b.close();
    }, [isOpen, handoffActive, flowLoading]);
    useEffect(() => {
        var _a;
        (_a = v2TransportRef.current) === null || _a === void 0 ? void 0 : _a.setDebug(options.testWidget && options.authToken ? {
            ownerToken: options.authToken,
            receive: (runId, debug) => { var _a; if (((_a = v2RunRef.current) === null || _a === void 0 ? void 0 : _a.run_id) === runId)
                setV2Debug(debug); },
        } : undefined);
    }, [options.testWidget, options.authToken]);
    useEffect(() => {
        if (authState !== "authorized" || !v2Session || !customerId)
            return;
        try {
            const raw = window.sessionStorage.getItem(`noa-v2-run-${options.projectId}-${customerId}`);
            const pendingRaw = window.sessionStorage.getItem(`noa-v2-request-${options.projectId}-${customerId}`);
            if (!raw && !pendingRaw)
                return;
            const pending = pendingRaw ? JSON.parse(pendingRaw) : null;
            if (pending)
                v2RequestRef.current = pending;
            const run = raw ? JSON.parse(raw) : null;
            setFlowLoading(true);
            flowSendLockRef.current = true;
            (run ? receiveV2Run(run) : sendToV2Backend(pending.message, pending.conversationId, pending.uiAction)).then(() => {
                window.sessionStorage.removeItem(`noa-v2-run-${options.projectId}-${customerId}`);
                window.sessionStorage.removeItem(`noa-v2-request-${options.projectId}-${customerId}`);
                v2RequestRef.current = null;
            }).catch(() => { }).finally(() => {
                setFlowLoading(false);
                flowSendLockRef.current = false;
            });
        }
        catch { }
    }, [authState, v2Session, customerId]);
    async function sendToFlowBackend(message, pendingId, overrides) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const isTestWidget = options.testWidget === true;
        if (authState !== "authorized" || (!isTestWidget && !customerId)) {
            setFlowLoading(false);
            flowSendLockRef.current = false;
            return;
        }
        const handoffConversation = overrides && overrides.conversationId !== undefined
            ? overrides.conversationId
            : flowState.conversationId || activeConversationId || null;
        if (handoffActive && handoffConversationId && handoffConversationId === handoffConversation) {
            setFlowLoading(false);
            flowSendLockRef.current = false;
            return;
        }
        const pendingKey = pendingId !== null && pendingId !== void 0 ? pendingId : pendingConversationId;
        const endpoint = `${normalizeBaseUrl(baseUrl)}/chat/chat/flows`;
        const conversationId = overrides && overrides.conversationId !== undefined
            ? overrides.conversationId
            : flowState.conversationId || activeConversationId || undefined;
        const pinnedEngine = conversationId && !conversationId.startsWith("pending-")
            ? v2EnginesRef.current[conversationId] || ((_a = conversations.find((item) => item.id === conversationId)) === null || _a === void 0 ? void 0 : _a.engine)
            : undefined;
        if ((pinnedEngine || conversationEngine) === "v2") {
            try {
                await sendToV2Backend(message, (conversationId === null || conversationId === void 0 ? void 0 : conversationId.startsWith("pending-")) ? undefined : conversationId || undefined);
            }
            catch {
                lastFailedFlowRequestRef.current = { message, pendingId: pendingKey, overrides };
                setFlowMessages((prev) => [...prev, { id: `error-${Date.now()}`, role: "assistant", content: t("widget.errors.connectFailed"),
                        error: true, retryable: true, createdAt: Date.now() }]);
            }
            finally {
                setFlowLoading(false);
                flowSendLockRef.current = false;
            }
            return;
        }
        const selectedFlowId = overrides && overrides.selectedFlowId !== undefined ? overrides.selectedFlowId : flowState.selectedFlowId;
        const flowRunId = overrides && overrides.flowRunId !== undefined ? overrides.flowRunId : flowState.flowRunId;
        const selectedPlaybookId = overrides && overrides.selectedPlaybookId !== undefined
            ? overrides.selectedPlaybookId
            : flowState.selectedPlaybookId;
        const currentStepId = overrides && overrides.currentStepId !== undefined ? overrides.currentStepId : flowState.currentStepId;
        const metadata = getCurrentPageMetadata(widgetPlacement);
        if (overrides === null || overrides === void 0 ? void 0 : overrides.clickedSuggestion) {
            metadata.clicked_suggestion = true;
        }
        const origin = typeof window !== "undefined" ? window.location.origin : undefined;
        const testConversationId = conversationId && !conversationId.startsWith("pending-") ? conversationId : undefined;
        const payload = {
            project_id: options.projectId,
            ...(isTestWidget
                ? {
                    test_widget: true,
                    test_conversation_id: testConversationId,
                }
                : {
                    customer_id: customerId,
                    conversation_id: conversationId,
                }),
            message,
            selected_flow_id: selectedFlowId || undefined,
            flow_run_id: flowRunId || undefined,
            ...(!isTestWidget
                ? {
                    selected_playbook_id: selectedPlaybookId || undefined,
                    current_step_id: currentStepId || undefined,
                }
                : {}),
            origin,
            metadata,
        };
        if (isTestWidget) {
            payload.overrides = {
                browser_search_reasoning_effort: browserSearchReasoningEffort,
            };
        }
        if (activeLanguage) {
            payload.language = activeLanguage;
        }
        if (!payload.conversation_id)
            delete payload.conversation_id;
        if (!payload.test_conversation_id)
            delete payload.test_conversation_id;
        if (!payload.selected_flow_id)
            delete payload.selected_flow_id;
        if (!payload.flow_run_id)
            delete payload.flow_run_id;
        if (!payload.selected_playbook_id)
            delete payload.selected_playbook_id;
        if (!payload.current_step_id)
            delete payload.current_step_id;
        let queuedAutoContinue = null;
        let keepLockedForAutoContinue = false;
        try {
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }
            const data = await response.json();
            if (data.customer_id && data.customer_id !== customerId) {
                setCustomerId(data.customer_id);
                try {
                    if (typeof window !== "undefined") {
                        window.localStorage.setItem(options.testWidget ? `${CUSTOMER_ID_KEY}-test-${options.projectId}` : CUSTOMER_ID_KEY, data.customer_id);
                    }
                }
                catch {
                    // ignore storage errors
                }
            }
            const responseConversationId = (_b = data.test_conversation_id) !== null && _b !== void 0 ? _b : data.conversation_id;
            const conversationId = (_d = (_c = responseConversationId !== null && responseConversationId !== void 0 ? responseConversationId : flowState.conversationId) !== null && _c !== void 0 ? _c : pendingKey) !== null && _d !== void 0 ? _d : null;
            if (conversationId && conversationId !== activeConversationId) {
                setActiveConversationId(conversationId);
            }
            if (conversationId) {
                const nowIso = new Date().toISOString();
                setConversations((prev) => {
                    var _a, _b, _c, _d, _e;
                    const existing = prev.find((item) => item.id === conversationId);
                    const pending = pendingKey ? prev.find((item) => item.id === pendingKey) : undefined;
                    const filtered = prev.filter((item) => item.id !== conversationId && (!pendingKey || item.id !== pendingKey));
                    const base = existing !== null && existing !== void 0 ? existing : pending;
                    const nextConversation = {
                        id: conversationId,
                        customer: (_c = (_b = (_a = base === null || base === void 0 ? void 0 : base.customer) !== null && _a !== void 0 ? _a : data.customer_id) !== null && _b !== void 0 ? _b : customerId) !== null && _c !== void 0 ? _c : "",
                        name: (_d = base === null || base === void 0 ? void 0 : base.name) !== null && _d !== void 0 ? _d : "",
                        createdAt: (_e = base === null || base === void 0 ? void 0 : base.createdAt) !== null && _e !== void 0 ? _e : nowIso,
                        lastMessageAt: nowIso,
                    };
                    return [nextConversation, ...filtered];
                });
                if (pendingKey) {
                    setPendingConversationId((prev) => (prev === pendingKey ? null : prev));
                }
            }
            if (data.blocked || data.trial_inactive) {
                const blockedMessage = typeof data.message === "string" && data.message.trim()
                    ? data.message.trim()
                    : accessMessageOnSend !== null && accessMessageOnSend !== void 0 ? accessMessageOnSend : t("widget.auth.notWorking");
                setFlowMessages((prev) => [
                    ...prev,
                    {
                        id: `assistant-${Date.now()}`,
                        role: "assistant",
                        content: blockedMessage,
                        createdAt: Date.now(),
                    },
                ]);
                setNextMessageSuggestions([]);
            }
            else {
                const nextConversationId = (_e = conversationId !== null && conversationId !== void 0 ? conversationId : flowState.conversationId) !== null && _e !== void 0 ? _e : null;
                const nextMissingFields = Array.isArray(data.flow_missing_fields) ? data.flow_missing_fields : [];
                const nextValidationIssues = Array.isArray(data.flow_validation_issues) ? data.flow_validation_issues : [];
                const nextFlowCompletion = typeof data.flow_completion_percent === "number" ? data.flow_completion_percent : null;
                const nextFlowIsComplete = typeof data.flow_is_complete === "boolean" ? data.flow_is_complete : null;
                const nextFlowStatus = typeof data.flow_status === "string" ? data.flow_status : null;
                const completedSelectedFlowId = normalizeSupportId(data.completed_selected_flow_id);
                const explicitRedirectFlowId = normalizeSupportId(data.redirect_on_completion_flow_id);
                const responseSelectedFlowId = normalizeSupportId(data.selected_flow_id);
                const inferredRedirectFlowId = completedSelectedFlowId && responseSelectedFlowId && completedSelectedFlowId !== responseSelectedFlowId
                    ? responseSelectedFlowId
                    : null;
                const redirectTargetFlowId = explicitRedirectFlowId !== null && explicitRedirectFlowId !== void 0 ? explicitRedirectFlowId : inferredRedirectFlowId;
                const nextSelectedFlowId = normalizeSupportId(redirectTargetFlowId !== null && redirectTargetFlowId !== void 0 ? redirectTargetFlowId : data.selected_flow_id);
                const nextFlowRunId = redirectTargetFlowId
                    ? normalizeSupportId(data.next_flow_run)
                    : normalizeSupportId(data.flow_run_id);
                const nextBookingFlowRunId = normalizeSupportId(data.booking_flow_run_id);
                const responseSelectedPlaybookId = data.support_session
                    ? normalizeSupportId((_f = data.support_session.selected_playbook_id) !== null && _f !== void 0 ? _f : data.support_session.playbook_id)
                    : selectedPlaybookId !== null && selectedPlaybookId !== void 0 ? selectedPlaybookId : null;
                const responseCurrentStepId = data.support_session
                    ? normalizeSupportId((_g = data.support_session.current_step_id) !== null && _g !== void 0 ? _g : data.support_session.step_id)
                    : currentStepId !== null && currentStepId !== void 0 ? currentStepId : null;
                const hasFlowMeta = Boolean(nextFlowStatus || data.flow_run_id || data.selected_flow_id);
                const hasSupportMeta = Boolean(data.support_session || responseSelectedPlaybookId || responseCurrentStepId);
                const handoffConversationId = nextConversationId !== null && nextConversationId !== void 0 ? nextConversationId : null;
                const nextHandoffActive = Boolean(data.handoff_active);
                const nextHandoffToken = typeof data.handoff_token === "string" ? data.handoff_token : null;
                const nextHandoffExpiresAt = typeof data.handoff_expires_at === "string" ? data.handoff_expires_at : null;
                const hasCalendarPayload = data.message_type === "calendar" && data.calendar && Array.isArray(data.calendar.slots);
                setFlowState((prev) => ({
                    ...prev,
                    conversationId: nextConversationId !== null && nextConversationId !== void 0 ? nextConversationId : prev.conversationId,
                    flowRunId: nextFlowRunId !== null && nextFlowRunId !== void 0 ? nextFlowRunId : (hasFlowMeta ? prev.flowRunId : null),
                    bookingFlowRunId: hasCalendarPayload ? nextBookingFlowRunId : null,
                    selectedFlowId: nextSelectedFlowId !== null && nextSelectedFlowId !== void 0 ? nextSelectedFlowId : (hasFlowMeta ? prev.selectedFlowId : null),
                    selectedPlaybookId: hasSupportMeta ? responseSelectedPlaybookId : prev.selectedPlaybookId,
                    currentStepId: hasSupportMeta ? responseCurrentStepId : prev.currentStepId,
                    flowStatus: nextFlowStatus !== null && nextFlowStatus !== void 0 ? nextFlowStatus : (hasFlowMeta ? prev.flowStatus : null),
                    flowIsComplete: nextFlowIsComplete,
                    flowCompletionPercent: nextFlowCompletion,
                    flowMissingFields: nextMissingFields,
                    flowValidationIssues: nextValidationIssues,
                    rawCompletion: typeof data.raw_completion === "string" ? data.raw_completion : null,
                    lastUpdated: Date.now(),
                }));
                if (handoffConversationId && nextHandoffActive && nextHandoffToken) {
                    const connectedUser = (_h = data.handoff_connected_user) !== null && _h !== void 0 ? _h : null;
                    const nextAgent = connectedUser && (connectedUser.username || connectedUser.image_url)
                        ? {
                            id: connectedUser.id,
                            username: connectedUser.username,
                            image_url: (_j = connectedUser.image_url) !== null && _j !== void 0 ? _j : null,
                        }
                        : null;
                    if (nextAgent) {
                        setHandoffAgent(nextAgent);
                    }
                    setHandoffConversationId(handoffConversationId);
                    setHandoffActive(true);
                    setHandoffToken(nextHandoffToken);
                    setHandoffExpiresAt(nextHandoffExpiresAt);
                    connectToHandoff(handoffConversationId, nextHandoffToken);
                }
                else if (handoffConversationId && data.live_support_requested) {
                    void fetchHandoffStatus(handoffConversationId);
                }
                const calendarPayload = data.message_type === "calendar" && data.calendar && Array.isArray(data.calendar.slots)
                    ? {
                        text: data.calendar.text,
                        timezone: data.calendar.timezone,
                        duration_minutes: data.calendar.duration_minutes,
                        slots: data.calendar.slots,
                    }
                    : null;
                const answer = data.answer || ((_k = data.calendar) === null || _k === void 0 ? void 0 : _k.text) || t("widget.errors.noResponse");
                const linkSources = normalizeLinkSources(data.sources);
                if (calendarPayload) {
                    setFlowMessages((prev) => [
                        ...prev,
                        {
                            id: `assistant-${Date.now()}`,
                            role: "assistant",
                            content: answer,
                            sources: linkSources,
                            products: normalizeChatProducts(data.products),
                            messageType: "calendar",
                            calendar: calendarPayload,
                            bookingFlowRunId: nextBookingFlowRunId,
                            createdAt: Date.now(),
                        },
                    ]);
                }
                else {
                    await streamAssistantMessage(answer, linkSources, normalizeChatProducts(data.products), setFlowMessages, setFlowStreamingId);
                }
                const nextSuggestions = calendarPayload
                    ? []
                    : normalizeChatSuggestions(data, {
                        selectedFlowId: nextSelectedFlowId,
                        selectedPlaybookId: responseSelectedPlaybookId,
                        currentStepId: responseCurrentStepId,
                    });
                setNextMessageSuggestions(nextSuggestions);
                const hasRedirectTarget = Boolean(redirectTargetFlowId);
                const hasContinuationContext = Boolean(nextSelectedFlowId || nextFlowRunId);
                const responseIsNextFlow = nextFlowStatus === "in_progress" && nextFlowIsComplete === false;
                const responseHasCreatedNextRun = Boolean(data.next_flow_run);
                const responseSelectedNextFlow = Boolean(inferredRedirectFlowId);
                const shouldAutoContinueRedirect = !calendarPayload &&
                    hasRedirectTarget &&
                    Boolean(nextConversationId) &&
                    hasContinuationContext &&
                    (responseIsNextFlow || responseHasCreatedNextRun || responseSelectedNextFlow);
                if (shouldAutoContinueRedirect) {
                    queuedAutoContinue = {
                        conversationId: nextConversationId,
                        selectedFlowId: nextSelectedFlowId,
                        flowRunId: nextFlowRunId,
                    };
                }
            }
            if (conversationId && !isTestWidget) {
                fetchConversations();
            }
            lastFailedFlowRequestRef.current = null;
            if (queuedAutoContinue) {
                setFlowLoading(true);
                flowSendLockRef.current = true;
                keepLockedForAutoContinue = true;
                window.setTimeout(() => {
                    var _a, _b, _c;
                    sendToFlowBackend(FLOW_REDIRECT_CONTINUE_MESSAGE, null, {
                        conversationId: (_a = queuedAutoContinue === null || queuedAutoContinue === void 0 ? void 0 : queuedAutoContinue.conversationId) !== null && _a !== void 0 ? _a : null,
                        selectedFlowId: (_b = queuedAutoContinue === null || queuedAutoContinue === void 0 ? void 0 : queuedAutoContinue.selectedFlowId) !== null && _b !== void 0 ? _b : null,
                        flowRunId: (_c = queuedAutoContinue === null || queuedAutoContinue === void 0 ? void 0 : queuedAutoContinue.flowRunId) !== null && _c !== void 0 ? _c : null,
                        selectedPlaybookId: null,
                        currentStepId: null,
                    });
                }, 0);
                return;
            }
        }
        catch (error) {
            console.error("Flow chat error", error);
            lastFailedFlowRequestRef.current = {
                message,
                pendingId: pendingKey,
                overrides,
            };
            if (pendingKey && pendingKey.startsWith("pending-")) {
                setPendingConversationId((prev) => (prev === pendingKey ? null : prev));
                setConversations((prev) => prev.filter((item) => item.id !== pendingKey));
            }
            setFlowMessages((prev) => [
                ...prev,
                {
                    id: `assistant-${Date.now()}`,
                    role: "assistant",
                    content: t("widget.errors.connectFailed"),
                    error: true,
                    retryable: true,
                    createdAt: Date.now(),
                },
            ]);
        }
        finally {
            setFlowStreamingId(null);
            if (typeof keepLockedForAutoContinue !== "undefined" && keepLockedForAutoContinue) {
                return;
            }
            setFlowLoading(false);
            flowSendLockRef.current = false;
        }
    }
    const configuredWidgetWidth = `${widgetSettings.width}px`;
    const configuredWidgetHeight = widgetSettings.height;
    const resolvedMobileViewportHeight = mobileViewportHeight || (typeof window !== "undefined" ? window.innerHeight : configuredWidgetHeight);
    const openWidth = isMobile ? "100vw" : configuredWidgetWidth;
    const openMaxWidth = isMobile ? "100vw" : "92vw";
    const closedWidth = "auto";
    const closedMaxWidth = "auto";
    const headerImage = agentImage;
    const handleChatBack = () => {
        if (typeof window !== "undefined") {
            const currentState = isWidgetHistoryState(window.history.state) && window.history.state.sessionId === historySessionIdRef.current
                ? window.history.state
                : null;
            if ((currentState === null || currentState === void 0 ? void 0 : currentState.mode) === "chat") {
                window.history.back();
                return;
            }
        }
        setView(lastEntryTab || "home");
    };
    const handleMinimize = () => {
        setSettingsOpen(false);
        setFlowInfoOpen(false);
        if (typeof window !== "undefined") {
            const currentState = isWidgetHistoryState(window.history.state) && window.history.state.sessionId === historySessionIdRef.current
                ? window.history.state
                : null;
            if ((currentState === null || currentState === void 0 ? void 0 : currentState.mode) === "chat") {
                window.history.go(-2);
                return;
            }
            if ((currentState === null || currentState === void 0 ? void 0 : currentState.mode) === "open") {
                window.history.back();
                return;
            }
        }
        setIsOpen(false);
    };
    const containerStyle = {
        position: options.floating ? "fixed" : "relative",
        top: options.floating && isMobile && isOpen ? mobileViewportOffsetTop : undefined,
        bottom: options.floating ? (isMobile && isOpen ? undefined : isMobile ? 0 : "18px") : undefined,
        right: isMobile ? 0 : options.floating && activeTheme.position === "bottom-right" ? "18px" : undefined,
        left: isMobile ? 0 : options.floating && activeTheme.position === "bottom-left" ? "18px" : undefined,
        width: isOpen ? openWidth : closedWidth,
        maxWidth: isOpen ? openMaxWidth : closedMaxWidth,
        zIndex: options.floating ? 9999 : "auto",
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        color: activeTheme.foreground,
        height: isMobile && isOpen ? resolvedMobileViewportHeight : undefined,
    };
    const scrollbarStyles = useMemo(() => {
        var _a;
        const track = (_a = activeTheme.background) !== null && _a !== void 0 ? _a : "#0f172a";
        const thumb = "#64748b";
        const thumbHover = "#94a3b8";
        return `
      .chatbot-widget-shell,
      .chatbot-widget-shell * {
        scrollbar-width: thin;
        scrollbar-color: ${thumb} ${track};
      }
      .chatbot-widget-shell *::-webkit-scrollbar {
        width: 6px;
        height: 6px;
      }
      .chatbot-widget-shell *::-webkit-scrollbar-track {
        background: ${track};
      }
      .chatbot-widget-shell *::-webkit-scrollbar-thumb {
        background-color: ${thumb};
        border-radius: 999px;
        border: 1px solid ${track};
      }
      .chatbot-widget-shell *::-webkit-scrollbar-thumb:hover {
        background-color: ${thumbHover};
      }
    `;
    }, [activeTheme.background]);
    const latestConversation = history[0];
    const latestConversationClickable = latestConversation && !latestConversation.id.startsWith("pending-");
    const openHistoryConversation = (item, origin) => {
        setLastEntryTab(origin);
        setView("chat");
        setFlowStreamingId(null);
        setFlowLoading(false);
        setFlowPendingPrompt(null);
        setFlowBadgesDismissed(false);
        setNextMessageSuggestions([]);
        setFlowInfoOpen(false);
        setIsOpen(true);
        if ((pendingConversationId && pendingConversationId === item.id) || item.id.startsWith("pending-")) {
            return;
        }
        setActiveConversationId(item.id);
        const storedFlowState = flowStatesByConversation[item.id];
        if (storedFlowState) {
            setFlowState(storedFlowState);
        }
        else {
            setFlowState({ ...defaultFlowState, conversationId: item.id });
        }
        const cached = conversationMessages[item.id];
        if (cached) {
            setFlowMessages(cached);
            setNextMessageSuggestions(getLastAssistantSuggestions(cached));
            return;
        }
        setFlowMessages([]);
        fetchMessagesForConversation(item.id, origin);
    };
    const activeMessages = flowMessages;
    const activeInput = flowInput;
    const activeLoading = flowLoading;
    const activeStreamingId = flowStreamingId;
    const activeInputDisabled = flowInputDisabled;
    const activeFlowBadges = flowBadges;
    const isExistingConversation = Boolean(activeConversationKey && !String(activeConversationKey).startsWith("pending-"));
    const showFlowBadges = activeFlowBadges.length > 0 &&
        !flowBadgesDismissed &&
        !flowInputDisabled &&
        !handoffActive &&
        !isExistingConversation;
    const activeInputChange = (value) => {
        setFlowInput(value);
        if (value.length > 0) {
            setFlowBadgesDismissed(true);
        }
    };
    const activeSend = handleFlowSend;
    const activeSuggestions = handoffActive ? [] : nextMessageSuggestions.slice(0, 3);
    const widgetPlacement = options.floating ? `floating_${activeTheme.position}` : "embedded_widget";
    const dashboardUrl = getWidgetAppOrigin();
    const isLightTheme = (activeTheme.foreground || "").toLowerCase() === "#0f172a";
    const shellShadowColor = isLightTheme ? "#cbd5e1" : "#020617";
    const mutedTextColor = isLightTheme ? "#64748b" : "#93a4bf";
    const accentTextColor = isLightTheme ? "#ffffff" : "#0b1020";
    const blockedContent = (_jsxs("div", { style: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 14,
            padding: "32px 28px",
            textAlign: "center",
        }, children: [_jsx("div", { style: { fontSize: 18, fontWeight: 700, color: activeTheme.contentColor }, children: t("widget.auth.activateTitle") }), _jsx("div", { style: { fontSize: 14, lineHeight: 1.6, color: mutedTextColor }, children: authMessage !== null && authMessage !== void 0 ? authMessage : t("widget.auth.startTrialEnable") }), _jsx("a", { href: dashboardUrl, target: "_blank", rel: "noreferrer", style: {
                    padding: "10px 16px",
                    borderRadius: 9999,
                    background: activeTheme.accentColor,
                    color: accentTextColor,
                    fontWeight: 700,
                    fontSize: 13,
                    textDecoration: "none",
                    boxShadow: `0 10px 24px ${shellShadowColor}`,
                }, children: t("widget.auth.goToDashboard") })] }));
    const tabContent = view === "help" ? (_jsx(HelpTab, { theme: activeTheme, baseUrl: baseUrl, t: t })) : view === "history" ? (_jsx(HistoryTab, { history: history, theme: activeTheme, t: t, onOpen: (item) => {
            openHistoryConversation(item, "history");
        }, onRename: (id, title) => {
            renameConversation(id, title);
        }, onDelete: (id) => {
            deleteConversation(id);
            if (activeConversationId === id) {
                setActiveConversationId(null);
                setPendingConversationId(null);
                setFlowInput("");
                setFlowMessages([createWelcomeMessage(t, resolvedAgentName, welcomeMessageText)]);
                setFlowState(defaultFlowState);
                setFlowInfoOpen(false);
                setView("home");
            }
        } })) : (_jsx(HomeTab, { theme: activeTheme, brandName: resolvedAgentName, agentImage: headerImage, heroTitle: homeHeroTitle, heroSubtitle: homeHeroSubtitle, welcomeMessage: welcomeMessageText, t: t, onStart: startNewFlowConversation, flows: activeFlowBadges, onPickFlow: (flow) => handleFlowBadgePick(flow, "home"), latestConversation: latestConversation !== null && latestConversation !== void 0 ? latestConversation : undefined, onOpenLatest: latestConversationClickable && latestConversation
            ? () => openHistoryConversation(latestConversation, "home")
            : undefined }));
    const shell = (_jsxs("div", { className: "chatbot-widget-shell", style: {
            background: activeTheme.background,
            border: "none",
            borderRadius: isMobile ? 0 : activeTheme.corner,
            overflow: "hidden",
            boxShadow: "none",
            height: isMobile ? resolvedMobileViewportHeight : configuredWidgetHeight,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            boxSizing: "border-box",
            paddingTop: 0,
            paddingBottom: isMobile ? 12 : 0,
        }, children: [_jsx("style", { children: scrollbarStyles }), !isChatting && (_jsxs("div", { style: {
                    position: "absolute",
                    top: 6,
                    right: 16,
                    zIndex: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                }, children: [_jsx("button", { "aria-label": "Open settings", onClick: () => setSettingsOpen(true), style: {
                            width: 28,
                            height: 28,
                            border: "none",
                            background: "transparent",
                            color: "#1f2937",
                            cursor: "pointer",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                            fontSize: 20,
                            lineHeight: 1,
                        }, children: "\u22EF" }), _jsx("button", { "aria-label": t("header.minimize"), onClick: handleMinimize, style: {
                            width: 28,
                            height: 28,
                            border: "none",
                            background: "transparent",
                            color: "#1f2937",
                            cursor: "pointer",
                            display: "grid",
                            placeItems: "center",
                            padding: 0,
                        }, children: _jsx("span", { style: { display: "block", width: 14, height: 1, background: "#1f2937", borderRadius: 999 } }) })] })), isChatting && (_jsx(Header, { brandName: resolvedAgentName, accentColor: activeTheme.accentColor, contentColor: activeTheme.contentColor, headerBackgroundColor: activeTheme.chatHeaderBackgroundColor, secondaryBackground: activeTheme.secondaryBackground, agentImage: headerImage, isChatting: true, showBack: true, onBack: handleChatBack, showInfo: true, onInfo: () => setSettingsOpen(true), infoLabel: "Open settings", onClose: handleMinimize, t: t })), _jsx("div", { style: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }, children: isBlocked ? (blockedContent) : isChatting ? (_jsx("div", { style: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }, children: _jsx("div", { style: { flex: 1, minHeight: 0, display: "flex" }, children: _jsx(ChatView, { brandName: resolvedAgentName, agentImage: headerImage, theme: activeTheme, t: t, messages: activeMessages, input: activeInput, onInputChange: activeInputChange, onSend: activeSend, flowBadges: activeFlowBadges, showFlowBadges: showFlowBadges, onPickFlow: (flow) => handleFlowBadgePick(flow, "chat"), messageSuggestions: activeSuggestions, onPickSuggestion: (suggestion) => {
                                draftSuggestionRef.current = suggestion;
                                activeInputChange(suggestion.message);
                            }, loading: activeLoading, activityLabel: v2Activity, streamingId: activeStreamingId, inputDisabled: activeInputDisabled, onRetryLast: handleRetryLastMessage, retryDisabled: activeInputDisabled || flowSendLockRef.current, showConsent: !isPreview && isOpen && isChatting && !consent.approved, onConfirmBooking: confirmBookingSlot, onApproveConsent: () => {
                                consent.approve();
                                if (flowPendingPrompt) {
                                    const pending = flowPendingPrompt;
                                    setFlowPendingPrompt(null);
                                    bypassConsentRef.current = true;
                                    handleFlowSend(pending.message, {
                                        selectedFlowId: pending.selectedFlowId,
                                        flowRunId: pending.flowRunId,
                                        forceNewConversation: pending.forceNewConversation,
                                        selectedPlaybookId: pending.selectedPlaybookId,
                                        currentStepId: pending.currentStepId,
                                        clickedSuggestion: pending.clickedSuggestion,
                                    });
                                }
                            } }) }) })) : (_jsx("div", { style: { flex: 1, minHeight: 0, overflowY: "auto" }, children: tabContent })) }), !isChatting && !isBlocked && (_jsx(TabNav, { active: (_b = view) !== null && _b !== void 0 ? _b : "home", onChange: (tab) => setView(tab), accentColor: activeTheme.activeCardBackgroundColor || activeTheme.accentColor, contentColor: activeTheme.contentColor, secondaryBackground: activeTheme.secondaryBackground, t: t })), _jsxs("div", { style: {
                    padding: "6px 10px",
                    fontSize: 11,
                    color: "#64748b",
                    textAlign: "center",
                    borderTop: "none",
                    background: "#f8fafc",
                }, children: ["Powered by", " ", _jsx("a", { href: dashboardUrl, target: "_blank", rel: "noreferrer", style: { color: "#1f2937", fontWeight: 600, textDecoration: "none" }, children: "Noa" })] }), _jsx(FlowDebugModal, { open: flowInfoOpen, onClose: () => setFlowInfoOpen(false), theme: activeTheme, info: flowState, agentDebug: options.testWidget && options.authToken ? v2Debug : null, t: t }), settingsOpen && (_jsx("div", { onClick: () => setSettingsOpen(false), style: {
                    position: "absolute",
                    inset: 0,
                    background: "rgba(15,23,42,0.25)",
                    display: "grid",
                    placeItems: "center",
                    padding: 16,
                    zIndex: 40,
                }, children: _jsxs("div", { onClick: (e) => e.stopPropagation(), style: {
                        width: "100%",
                        maxWidth: 320,
                        borderRadius: 14,
                        border: "1px solid #d8e0eb",
                        background: "#ffffff",
                        color: "#0f172a",
                        padding: 14,
                        display: "grid",
                        gap: 12,
                    }, children: [_jsx("div", { style: { fontWeight: 700 }, children: "Settings" }), options.testWidget ? (_jsxs("div", { style: { display: "grid", gap: 8 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: "Website search reasoning" }), _jsxs("select", { value: browserSearchReasoningEffort, onChange: (e) => {
                                        const next = normalizeBrowserSearchReasoningEffort(e.target.value) || "medium";
                                        setBrowserSearchReasoningEffort(next);
                                    }, style: {
                                        width: "100%",
                                        borderRadius: 10,
                                        border: "1px solid #d8e0eb",
                                        background: "#f8fafc",
                                        color: "#1f2937",
                                        padding: "10px 10px",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        textTransform: "capitalize",
                                        cursor: "pointer",
                                    }, children: [_jsx("option", { value: "low", children: "Low" }), _jsx("option", { value: "medium", children: "Medium" }), _jsx("option", { value: "high", children: "High" })] })] })) : null, _jsxs("div", { style: { display: "grid", gap: 8 }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 600 }, children: "Sound" }), _jsxs("div", { style: { display: "grid", gridTemplateColumns: "126px minmax(0,1fr)", gap: 8 }, children: [_jsxs("button", { type: "button", onClick: toggleSoundPreference, "aria-label": soundEnabled ? "Disable sound" : "Enable sound", style: {
                                                borderRadius: 10,
                                                border: soundEnabled ? "1px solid #2563eb" : "1px solid #d8e0eb",
                                                background: soundEnabled ? "rgba(37,99,235,0.1)" : "#f8fafc",
                                                color: soundEnabled ? "#2563eb" : "#1f2937",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                padding: "10px 10px",
                                                fontSize: 13,
                                                fontWeight: 600,
                                            }, children: [_jsx("span", { children: soundEnabled ? "Enabled" : "Disabled" }), soundEnabled ? (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M4 10v4h4l5 4V6L8 10H4z", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M17 9a4 4 0 0 1 0 6", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] })) : (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M4 10v4h4l5 4V6L8 10H4z", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M17 9l4 4m0-4l-4 4", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }))] }), _jsx("select", { value: soundVariant, disabled: !soundEnabled, onChange: (e) => {
                                                const next = parseSoundVariant(e.target.value, "classic");
                                                selectSoundVariant(next);
                                                void playNotificationSound(next);
                                            }, style: {
                                                width: "100%",
                                                borderRadius: 10,
                                                border: "1px solid #d8e0eb",
                                                background: soundEnabled ? "#f8fafc" : "#eef2f7",
                                                color: soundEnabled ? "#1f2937" : "#94a3b8",
                                                padding: "10px 10px",
                                                fontSize: 13,
                                                fontWeight: 600,
                                                textTransform: "capitalize",
                                                cursor: soundEnabled ? "pointer" : "not-allowed",
                                            }, children: SOUND_VARIANTS.map((variant) => (_jsx("option", { value: variant, children: variant }, variant))) })] })] })] }) }))] }));
    if (authState === "loading") {
        return null;
    }
    if (authState === "unauthorized") {
        if (authError) {
            console.warn("Chatbot widget authentication failed:", authError);
        }
        return null;
    }
    return (_jsx(WidgetProvider, { value: { configuration, projectId: options.projectId, publicKey: options.publicKey, customerId }, children: _jsx("div", { style: containerStyle, children: isOpen ? (shell) : (_jsxs("div", { style: {
                    display: "flex",
                    alignItems: activeTheme.position === "bottom-left" ? "flex-start" : "flex-end",
                    gap: 8,
                    justifyContent: activeTheme.position === "bottom-left" ? "flex-start" : "flex-end",
                    flexDirection: "column",
                }, children: [showFlowsWhenClosed && !flowsDismissed && flowBadges.length > 0 && flowCardVisible && (_jsx(ClosedFlowCard, { agentName: resolvedAgentName, agentImage: headerImage, message: welcomeMessageText, flows: activeFlowBadges, onPick: (flow) => handleFlowBadgePick(flow, "closed"), onDismiss: dismissClosedFlows, align: activeTheme.position === "bottom-left" ? "left" : "right", contentColor: activeTheme.closedFlowsTextColor || activeTheme.contentColor, surfaceColor: activeTheme.closedFlowsBackgroundColor || activeTheme.secondaryBackground, suggestionColor: activeTheme.closedFlowsSuggestionColor, t: t })), _jsx("button", { "aria-label": t("widget.openChatbot"), onClick: () => {
                            dismissClosedFlows();
                            setSettingsOpen(false);
                            setFlowInfoOpen(false);
                            setIsOpen(true);
                        }, style: {
                            width: 62,
                            height: 62,
                            borderRadius: "50%",
                            background: "transparent",
                            color: accentTextColor,
                            backgroundImage: `url(${launcherImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            border: "none",
                            boxShadow: "none",
                            fontWeight: 800,
                            cursor: "pointer",
                        } })] })) }) }));
}
